import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import {
  ArrowLeft, Server, Network, Shield, Mail, Globe, Plus, Trash2, Copy,
  Inbox, ExternalLink, RefreshCw, Edit3, Save, X, RotateCw, CheckCircle2, XCircle, AlertTriangle, Search,
  Download, Upload, ChevronDown, ChevronRight, Activity, HardDrive
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Visão Geral',   icon: Activity  },
  { id: 'zone',     label: 'Zona DNS',      icon: Network },
  { id: 'reverse',  label: 'Reverso',       icon: RotateCw},
  { id: 'dnssec',   label: 'DNSSEC',        icon: Shield  },
  { id: 'rpki',     label: 'RPKI',          icon: Shield  },
  { id: 'geofeed',  label: 'Geofeed',       icon: Globe   },
  { id: 'email',    label: 'E-mail',        icon: Mail    },
  { id: 'webmail',  label: 'Webmail',       icon: Inbox   },
  { id: 'nginx',    label: 'Nginx (Proxy)', icon: Server  },
  { id: 'speedtest',label: 'Speedtest',     icon: Activity},
  { id: 'mc',       label: 'Minha Conexão', icon: Activity},
];

export default function DomainDetail() {
  const { name } = useParams();
  const [tab, setTab] = useState('overview');
  const [domain, setDomain] = useState(null);
  const [err, setErr] = useState('');

  const reload = () => api.get(`/domains/${name}`).then(r => setDomain(r.data));
  useEffect(() => { reload().catch(e => setErr(e.response?.data?.error || 'Erro')); }, [name]);

  if (err) return <><p className="err-msg">{err}</p><Link to="/domains"><button className="btn btn-secondary"><ArrowLeft size={14}/> Voltar</button></Link></>;
  if (!domain) return <p className="dim">Carregando…</p>;

  return (
    <div className="stack">
      <div>
        <Link to="/domains" style={{color:'var(--text-mute)', textDecoration:'none', fontSize:13, display:'inline-flex', alignItems:'center', gap:4}}>
          <ArrowLeft size={13}/> Domínios
        </Link>
        <div className="row-between" style={{marginTop:8}}>
          <div>
            <div className="hrow" style={{gap:10}}>
              <h1 className="h1">{domain.name}</h1>
              {domain.has_dns && <span className="badge ok lg"><span className="dot ok"/>Saudável</span>}
              {domain.has_dnssec && <span className="badge accent"><Shield size={11}/>DNSSEC</span>}
            </div>
            <div className="hrow dim" style={{fontSize:12.5, marginTop:6, gap:18, flexWrap:'wrap'}}>
              <span className="mono">{domain.ipv4}</span>
              {domain.ipv6 && <><span>·</span><span className="mono">{domain.ipv6}</span></>}
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-shell">
        <nav className="tabs-rail">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id}
                className={'tab-item ' + (tab===t.id?'active':'')}
                onClick={()=>setTab(t.id)}>
                <Icon size={15} className="tab-icon"/>
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="stack">
          {tab === 'overview' && <Overview domain={domain} />}
          {tab === 'zone'     && <Zone     domain={domain} />}
          {tab === 'reverse'  && <Reverse  domain={domain} />}
          {tab === 'dnssec'   && <Dnssec   domain={domain} />}
          {tab === 'rpki'     && <RpkiTab  domain={domain} />}
          {tab === 'geofeed'  && <GeofeedTab domain={domain} />}
          {tab === 'email'    && <Email    domain={domain} />}
          {tab === 'webmail'  && <Webmail  domain={domain} onChange={reload} />}
          {tab === 'nginx'    && <Nginx    domain={domain} />}
          {tab === 'speedtest'&& <SpeedtestTab domain={domain} />}
          {tab === 'mc'       && <MinhaConexaoTab domain={domain} />}
        </div>
      </div>
    </div>
  );
}

// ── VISÃO GERAL ─────────────────────────────────────────────────
function Overview({ domain }) {
  const [config, setConfig] = useState(null);
  const [health, setHealth] = useState(null);
  const [hLoading, setHLoading] = useState(false);

  const loadHealth = async (force = false) => {
    setHLoading(true);
    try {
      const r = await api.get(`/domains/${domain.name}/health${force ? '?force=1' : ''}`);
      setHealth(r.data);
    } finally { setHLoading(false); }
  };
  // Carrega o config rapidamente. Health só é carregado se houver cache (rápido).
  useEffect(() => {
    api.get('/server/config').then(r => setConfig(r.data)).catch(()=>{});
    loadHealth(false);
  }, [domain.name]);

  return (
    <>
      <div className="cards">
        <div className="card"><div className="label">DNS</div><div className={'value ' + (domain.has_dns ? 'ok' : 'err')}>{domain.has_dns ? 'Ativo' : 'Desativado'}</div></div>
        <div className="card"><div className="label">DNSSEC</div><div className={'value ' + (domain.has_dnssec ? 'ok' : 'err')}>{domain.has_dnssec ? 'Ativo' : 'Desativado'}</div></div>
        <div className="card"><div className="label">E-mail</div><div className={'value ' + (domain.has_email ? 'ok' : 'err')}>{domain.has_email ? 'Ativo' : 'Desativado'}</div></div>
        <div className="card"><div className="label">Webmail</div><div className={'value ' + (domain.has_webmail ? 'ok' : 'err')}>{domain.has_webmail ? 'Ativo' : 'Desativado'}</div></div>
      </div>

      {/* ── Health check ─────────────────────────────── */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'1.5rem'}}>
        <h3 style={{margin:0}}>Diagnóstico do Domínio {health?.cached && <span style={{fontSize:'.7rem', color:'#a1a1aa', fontWeight:'normal'}}> (em cache — até 5 min)</span>}</h3>
        <button onClick={() => loadHealth(true)} className="ghost" disabled={hLoading}>
          <RefreshCw size={14}/> {hLoading ? 'Verificando…' : 'Re-verificar agora'}
        </button>
      </div>
      {health ? (
        <>
          <div style={{padding:'.6rem 1rem', background: health.allOk ? 'rgba(16,185,129,.1)' : 'rgba(245,158,11,.1)',
            border:'1px solid ' + (health.allOk ? '#10b981' : '#f59e0b'), borderRadius:'8px', marginTop:'.6rem', marginBottom:'.6rem', fontSize:'.9rem'}}>
            {health.allOk ? '✅ Todos os checks passaram — domínio 100% operacional.'
                          : '⚠️  Alguns checks falharam — provavelmente delegação ou DS ainda não propagaram.'}
          </div>
          <table>
            <thead><tr><th>Check</th><th style={{width:'90px'}}>Status</th><th>Detalhe</th></tr></thead>
            <tbody>
              {health.checks.map((c, i) => (
                <tr key={i}>
                  <td>{c.name}</td>
                  <td><span className={'badge ' + (c.ok ? 'ok' : 'err')}>{c.ok ? 'OK' : 'FALHOU'}</span></td>
                  <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem', color:'#a1a1aa'}}>{c.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : <p style={{color:'#a1a1aa'}}>Executando diagnóstico…</p>}

      {/* ── Nameservers ─────────────────────────────── */}
      <h3 style={{marginTop:'1.5rem'}}>Nameservers Autoritativos</h3>
      <table>
        <thead><tr><th>Servidor</th><th>Função</th><th>IPv4</th><th>IPv6</th></tr></thead>
        <tbody>
          <tr><td><strong>ns1.{domain.name}</strong></td><td><span className="badge ok">Master + Recursivo</span></td>
              <td style={{fontFamily:'JetBrains Mono'}}>{config?.IPV4_DNS1 || '–'}</td>
              <td style={{fontFamily:'JetBrains Mono'}}>{config?.IPV6_DNS1 || '–'}</td></tr>
          <tr><td><strong>ns2.{domain.name}</strong></td><td><span className="badge ok">Slave + Recursivo</span></td>
              <td style={{fontFamily:'JetBrains Mono'}}>{config?.IPV4_DNS2 || '–'}</td>
              <td style={{fontFamily:'JetBrains Mono'}}>{config?.IPV6_DNS2 || '–'}</td></tr>
        </tbody>
      </table>

      <div style={{marginTop:'1rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px'}}>
        <strong style={{color:'#3b82f6'}}>📌 Para delegar este domínio</strong>
        <p style={{marginTop:'.5rem', fontSize:'.88rem', color:'#a1a1aa'}}>
          No registrador, configure os nameservers <code>ns1.{domain.name}</code> e <code>ns2.{domain.name}</code> com os IPs acima como <strong>glue records</strong>.
          Em seguida, vá em <strong>DNSSEC</strong> e copie o DS record para o registrador.
        </p>
      </div>
    </>
  );
}

// ── ZONA DNS ────────────────────────────────────────────────────
const DNS_TYPES = ['A','AAAA','CNAME','MX','TXT','NS','SRV','CAA'];

function Zone({ domain }) {
  const [data, setData] = useState({ content: '', records: [] });
  const [view, setView] = useState('table');
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'A', value: '', ttl: '3600' });
  const [msg, setMsg] = useState('');

  const load = () => api.get(`/dns/${domain.name}/records`).then(r => setData(r.data));
  useEffect(() => { load(); }, [domain.name]);

  const add = async () => {
    setMsg('Adicionando…');
    try {
      await api.post(`/dns/${domain.name}/records`, form);
      setMsg('OK');
      setShow(false);
      setForm({ name: '', type: 'A', value: '', ttl: '3600' });
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  const del = async (r) => {
    if (!window.confirm(`Remover registro ${r.name} ${r.type} ${r.value}?`)) return;
    try {
      await api.delete(`/dns/${domain.name}/records`, { data: { name: r.name, type: r.type, value: r.value } });
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  // Placeholder do "value" varia por tipo
  const valuePlaceholder = {
    A: '192.0.2.1', AAAA: '2001:db8::1', CNAME: 'destino.exemplo.com.',
    MX: '10 mail.exemplo.com.', TXT: 'v=spf1 -all', NS: 'ns3.exemplo.com.',
    SRV: '0 5 5060 sip.exemplo.com.', CAA: '0 issue "letsencrypt.org"',
  }[form.type] || '';

  return (
    <>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem'}}>
        <div className="tab-bar" style={{margin:0, border:'none'}}>
          <div className={'tab ' + (view==='table'?'active':'')} onClick={()=>setView('table')}>Registros</div>
          <div className={'tab ' + (view==='raw'?'active':'')} onClick={()=>setView('raw')}>Arquivo bruto</div>
        </div>
        <button onClick={()=>setShow(true)}><Plus size={14}/> Adicionar Registro</button>
      </div>

      {view === 'table' ? (
        <table>
          <thead><tr><th>Nome</th><th>TTL</th><th>Tipo</th><th>Valor</th><th></th></tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={i}>
                <td style={{fontFamily:'JetBrains Mono'}}>{r.name}</td>
                <td style={{color:'#a1a1aa'}}>{r.ttl || '–'}</td>
                <td><span className="badge ok">{r.type}</span></td>
                <td style={{fontFamily:'JetBrains Mono', fontSize:'.82rem', wordBreak:'break-all'}}>{r.value}</td>
                <td>
                  {/* Bloqueia delete dos NS root + SOA-related para evitar quebrar a zona */}
                  {(['NS'].includes(r.type) && r.name === '@')
                    ? <span style={{color:'#52525b', fontSize:'.75rem'}}>(protegido)</span>
                    : <button className="danger" onClick={()=>del(r)}><Trash2 size={12}/></button>}
                </td>
              </tr>
            ))}
            {data.records.length === 0 && <tr><td colSpan={5} style={{color:'#a1a1aa'}}>(sem registros)</td></tr>}
          </tbody>
        </table>
      ) : (
        <pre className="code" style={{maxHeight:'500px', overflow:'auto'}}>{data.content || '(vazio)'}</pre>
      )}

      {show && (
        <div className="modal-bg" onClick={()=>setShow(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h2>Adicionar Registro DNS</h2>
            <div className="row">
              <div>
                <label>Nome</label>
                <input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="vpn  (ou @ para o domínio raiz)" />
              </div>
              <div style={{maxWidth:'130px'}}>
                <label>TTL</label>
                <input value={form.ttl} onChange={e=>setForm({...form, ttl: e.target.value})} placeholder="3600" />
              </div>
              <div style={{maxWidth:'140px'}}>
                <label>Tipo</label>
                <select value={form.type} onChange={e=>setForm({...form, type: e.target.value})}>
                  {DNS_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <label>Valor</label>
            <input value={form.value} onChange={e=>setForm({...form, value: e.target.value})}
              placeholder={valuePlaceholder} style={{fontFamily:'JetBrains Mono'}} />
            <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.5rem'}}>
              {form.type === 'CNAME' || form.type === 'MX' || form.type === 'NS' || form.type === 'SRV'
                ? 'Use FQDN terminado em ponto (ex: destino.exemplo.com.)' : ''}
              {form.type === 'TXT' ? 'As aspas serão adicionadas automaticamente se necessário.' : ''}
            </p>
            {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro') ? '#ef4444':'#10b981'}}>{msg}</div>}
            <div className="modal-actions">
              <button className="secondary" onClick={()=>setShow(false)}>Cancelar</button>
              <button onClick={add} disabled={!form.name || !form.value}>Adicionar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── REVERSO (IPv4 + IPv6) ───────────────────────────────────────
function Reverse({ domain }) {
  const [selectedAsn, setSelectedAsn] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (selectedAsn) {
    return <ReverseAsnDetail
      domain={domain}
      asn={selectedAsn}
      onBack={() => { setSelectedAsn(null); setRefreshKey(k => k+1); }}
    />;
  }
  return <ReverseAsnList domain={domain} key={refreshKey} onSelect={setSelectedAsn} />;
}

function ReverseAsnList({ domain, onSelect }) {
  const [asns, setAsns] = useState(null);
  useEffect(() => {
    api.get(`/domains/${domain.name}/asns`).then(r => setAsns(r.data));
  }, [domain.name]);

  if (asns === null) return <p style={{color:'#a1a1aa'}}>Carregando…</p>;

  return (
    <>
      <div>
        <h3 style={{margin:0}}>Reverso (DNS PTR) — por ASN</h3>
        <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
          Cada ASN tem seus prefixos e zonas reversas isoladas. Clique no ASN para gerenciar as zonas e PTRs.
        </p>
      </div>

      {asns.length === 0 ? (
        <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', marginTop:'1rem'}}>
          <strong style={{color:'#f59e0b'}}>Nenhum ASN vinculado</strong>
          <p style={{color:'#a1a1aa', margin:'.3rem 0 0', fontSize:'.85rem'}}>
            Vá na aba <strong>RPKI</strong> e adicione um ASN com seus prefixos. Depois gerencie reverso aqui.
          </p>
        </div>
      ) : (
        <table style={{marginTop:'1rem'}}>
          <thead><tr><th>Domínio</th><th>Organização</th><th>ASN</th><th>Prefixos</th><th></th></tr></thead>
          <tbody>
            {asns.map(a => (
              <tr key={a.id} onClick={() => onSelect(a)} style={{cursor:'pointer'}}>
                <td><strong>{domain.name}</strong></td>
                <td>{a.org_name || '–'}</td>
                <td style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</td>
                <td>
                  {(a.prefixes || []).map(p => (
                    <span key={p.id} className="badge ok" style={{marginRight:'.3rem', fontFamily:'JetBrains Mono'}}>{p.prefix}</span>
                  ))}
                  {!a.prefixes?.length && <span style={{color:'#a1a1aa'}}>sem prefixos</span>}
                </td>
                <td><button className="secondary"><Search size={12}/> Gerenciar Reverso</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function ReverseAsnDetail({ domain, asn, onBack }) {
  const [sub, setSub] = useState('v4');
  const [allZones, setAllZones] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ cidr: '', description: '' });
  const [msg, setMsg] = useState('');
  const [toast, setToast] = useState(null);
  const [showTestAll, setShowTestAll] = useState(false);

  const showToast = (kind, text, sticky = false) => {
    setToast({ kind, text });
    if (!sticky) setTimeout(() => setToast(null), 6000);
  };

  const load = () => api.get('/dns/reverse/zones').then(r => setAllZones(r.data));
  useEffect(() => { load(); }, []);

  // Verifica se zona reversa "pertence" a este ASN (está contida em algum prefixo dele)
  const belongsToAsn = (zoneCidr) => {
    return (asn.prefixes || []).some(p => isSubnetOrEqual(zoneCidr, p.prefix));
  };

  // Mostra todas — distingue entre as do ASN e órfãs apenas com um badge na linha.
  const filtered = allZones.filter(z => sub === 'v4' ? z.type === 'ipv4' : z.type === 'ipv6');
  const orphanCount = filtered.filter(z => !belongsToAsn(z.cidr)).length;

  // Prefixos do ASN nesta família que ainda não têm zona reversa criada
  const myPrefixes = (asn.prefixes || []).filter(p => (sub === 'v4' ? p.family === 'ipv4' : p.family === 'ipv6'));
  const missingPrefixes = myPrefixes.filter(p => !allZones.some(z => z.cidr === p.prefix));

  const add = async (cidr, description) => {
    setMsg('Criando…');

    // Validação local: CIDR deve pertencer a algum prefixo do ASN
    const fitsAsn = (asn.prefixes || []).some(p => isSubnetOrEqual(cidr, p.prefix));
    if (!fitsAsn && (asn.prefixes || []).length > 0) {
      setMsg('');
      showToast('warn',
        `O CIDR ${cidr} NÃO está contido nos prefixos cadastrados do ASN ${asn.asn} ` +
        `(${(asn.prefixes||[]).map(p=>p.prefix).join(', ')}). ` +
        `A zona será criada mas só aparecerá em outro ASN cujo prefixo contenha ${cidr}.`, true);
      // Continua e cria mesmo assim
    }

    try {
      const r = await api.post('/dns/reverse/zones', { cidr, description: description || '' });
      setMsg('');
      setShow(false);
      setForm({ cidr: '', description: '' });
      await load();
      showToast('ok', `✓ Zona criada: ${cidr} → ${r.data.zone_name} (id=${r.data.id})`);
    } catch (e) {
      const errMsg = e.response?.data?.error || 'Erro desconhecido';
      const isConflict = e.response?.status === 409;
      setMsg(errMsg);
      showToast(isConflict ? 'warn' : 'err', (isConflict ? '⚠ ' : '✗ ') + errMsg, true);
    }
  };

  const del = async (z) => {
    if (!window.confirm(`Remover zona reversa ${z.cidr}? (PTRs também serão removidos)`)) return;
    await api.delete(`/dns/reverse/zones/${z.id}`); load();
  };

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position:'fixed', top:'1rem', right:'1rem', maxWidth:'500px', zIndex:9999,
          padding:'1rem 1.2rem',
          background: toast.kind === 'ok' ? 'rgba(16,185,129,.15)' : toast.kind === 'err' ? 'rgba(239,68,68,.15)' : 'rgba(245,158,11,.15)',
          border:'1px solid ' + (toast.kind === 'ok' ? '#10b981' : toast.kind === 'err' ? '#ef4444' : '#f59e0b'),
          borderRadius:'8px', backdropFilter:'blur(8px)', boxShadow:'0 10px 40px rgba(0,0,0,.5)',
          display:'flex', alignItems:'flex-start', gap:'.6rem'
        }}>
          <span style={{flex:1, fontSize:'.88rem'}}>{toast.text}</span>
          <button onClick={()=>setToast(null)} className="ghost" style={{padding:'.1rem .3rem'}}><X size={14}/></button>
        </div>
      )}

      <button onClick={onBack} className="secondary" style={{marginBottom:'1rem'}}>
        <ArrowLeft size={14}/> Voltar para lista de ASNs
      </button>

      {/* Header */}
      <div style={{padding:'1.2rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', marginBottom:'1.5rem'}}>
        <div style={{color:'#a1a1aa', fontSize:'.78rem', textTransform:'uppercase', letterSpacing:'.1em'}}>{domain.name} · Reverso</div>
        <div style={{fontFamily:'JetBrains Mono', color:'#3b82f6', fontSize:'1.5rem', fontWeight:'700'}}>{asn.asn}</div>
        <div style={{color:'#a1a1aa'}}>{asn.org_name || 'Sem organização'}</div>
        <div style={{marginTop:'.5rem'}}>
          {(asn.prefixes || []).map(p => (
            <span key={p.id} className="badge ok" style={{marginRight:'.3rem', fontFamily:'JetBrains Mono'}}>{p.prefix}</span>
          ))}
        </div>
      </div>

      <div className="tab-bar" style={{marginBottom:'1rem'}}>
        <div className={'tab ' + (sub==='v4'?'active':'')} onClick={()=>setSub('v4')}>IPv4</div>
        <div className={'tab ' + (sub==='v6'?'active':'')} onClick={()=>setSub('v6')}>IPv6</div>
      </div>

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem'}}>
        <div>
          <h3 style={{margin:0}}>Zonas Reversas {sub === 'v4' ? 'IPv4' : 'IPv6'} de {asn.asn}</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem', maxWidth:'700px'}}>
            {sub === 'v4'
              ? 'Suporta /8 a /32. Sub-blocos /25-/32 usam RFC 2317. Útil para separar por categoria.'
              : 'Prefixo arredondado para múltiplo de 4 (nibble). Comumente /48, /56, /64.'}
          </p>
        </div>
        <div style={{display:'flex', gap:'.5rem'}}>
          {filtered.length > 0 && (
            <button onClick={()=>setShowTestAll(true)}
              style={{background:'#10b981'}}
              title="Testar todas as zonas reversas deste ASN">
              <CheckCircle2 size={14}/> Verificar Reverso
            </button>
          )}
          <button onClick={()=>{ setShow(true); setForm({ cidr:'', description:'' }); }}>
            <Plus size={14}/> Nova Zona Reversa
          </button>
        </div>
      </div>

      {orphanCount > 0 && (
        <div style={{marginBottom:'1rem', padding:'.5rem .9rem', background:'rgba(245,158,11,.08)', border:'1px solid #f59e0b33', borderRadius:'6px', fontSize:'.8rem', color:'#a1a1aa'}}>
          {orphanCount} zona(s) listada(s) fora dos prefixos deste ASN (marcadas com <span className="badge warn" style={{verticalAlign:'middle'}}>órfã</span>).
        </div>
      )}

      {/* Sugestões: prefixos do ASN sem zona reversa */}
      {missingPrefixes.length > 0 && (
        <div style={{marginBottom:'1rem', padding:'.8rem 1rem', background:'rgba(59,130,246,.08)', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
          <strong style={{color:'#3b82f6'}}>💡 Criar zona reversa dos prefixos cadastrados deste ASN:</strong>
          {missingPrefixes.map(p => (
            <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.3rem 0'}}>
              <code style={{fontSize:'.82rem'}}>{p.prefix}</code>
              <button onClick={() => add(p.prefix, p.description)} className="secondary">
                <Plus size={12}/> Criar zona
              </button>
            </div>
          ))}
        </div>
      )}

      <table>
        <thead><tr><th style={{width:'24px'}}></th><th>CIDR</th><th>Zona BIND9</th><th>Local</th><th>Externo</th><th>Descrição</th><th>Ações</th></tr></thead>
        <tbody>
          {filtered.map(z => (
            <ReverseZoneRow
              key={z.id}
              z={z}
              isOrphan={!belongsToAsn(z.cidr)}
              onDelete={del}
              suggestSuffix={domain.name}
              onChanged={load}
            />
          ))}
          {filtered.length === 0 && <tr><td colSpan={7} style={{color:'#a1a1aa'}}>Nenhuma zona {sub==='v4'?'IPv4':'IPv6'} cadastrada.</td></tr>}
        </tbody>
      </table>

      {filtered.length > 0 && (
        <div style={{marginTop:'1rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
          <strong style={{color:'#3b82f6'}}>📌 Externo "pendente"?</strong>
          <p style={{margin:'.5rem 0 0', color:'#a1a1aa'}}>
            Funciona localmente — mas o mundo só vê PTR depois que o LACNIC/Registro.br
            delegar a zona reversa aos seus NS. Configure no painel do Registro.br no bloco IP correspondente.
          </p>
        </div>
      )}

      {show && (
        <div className="modal-bg" onClick={()=>setShow(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h2>Nova Zona Reversa {sub === 'v4' ? 'IPv4' : 'IPv6'} — {asn.asn}</h2>
            <label>CIDR (rede/prefixo)</label>
            <input value={form.cidr} onChange={e=>setForm({...form, cidr: e.target.value})}
              placeholder={sub === 'v4' ? '203.0.113.0/24' : '2001:db8::/48'}
              style={{fontFamily:'JetBrains Mono'}} />
            <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
              Prefixos cadastrados do ASN: {myPrefixes.map(p => <code key={p.id} style={{marginRight:'.4rem'}}>{p.prefix}</code>)}
            </p>
            <label>Descrição (opcional)</label>
            <input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
              placeholder={sub === 'v4' ? 'Ex: Clientes residenciais' : 'Ex: Alocação IPv6 principal'} />
            {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro') ? '#ef4444':'#10b981'}}>{msg}</div>}
            <div className="modal-actions">
              <button className="secondary" onClick={()=>setShow(false)}>Cancelar</button>
              <button onClick={()=>add(form.cidr, form.description)} disabled={!form.cidr}>Criar</button>
            </div>
          </div>
        </div>
      )}

      {showTestAll && <ReverseTestAllModal asn={asn} zones={filtered} onClose={()=>setShowTestAll(false)} />}
    </>
  );
}

function ReverseTestAllModal({ asn, zones, onClose }) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  const runAll = async () => {
    setLoading(true);
    setResults({});
    const out = {};
    for (const z of zones) {
      try {
        const r = await api.get(`/dns/reverse/zones/${z.id}/test`);
        out[z.id] = r.data;
      } catch (e) { out[z.id] = { error: e.response?.data?.error || 'erro' }; }
      setResults({ ...out });
    }
    setLoading(false);
  };
  useEffect(() => { runAll(); }, []);

  const summary = Object.values(results).reduce((acc, r) => {
    if (r?.error) acc.error++;
    else if (r?.summary?.all_ok) acc.ok++;
    else if (r?.summary?.local_working) acc.partial++;
    else acc.fail++;
    return acc;
  }, { ok: 0, partial: 0, fail: 0, error: 0 });

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'700px', maxWidth:'95vw', maxHeight:'90vh', overflow:'auto'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            <h2 style={{margin:0}}>Verificação Geral do Reverso — {asn.asn}</h2>
            <p style={{color:'#a1a1aa', fontSize:'.82rem', margin:'.2rem 0 0'}}>
              {zones.length} zona(s) reversa(s) sendo testada(s)
            </p>
          </div>
          <button onClick={onClose} className="ghost"><X size={18}/></button>
        </div>

        {/* Resumo agregado */}
        <div className="cards" style={{marginTop:'1rem'}}>
          <div className="card">
            <div className="label">Totalmente OK</div>
            <div className="value ok">{summary.ok}</div>
          </div>
          <div className="card">
            <div className="label">Parcial (só local)</div>
            <div className="value" style={{color:'#f59e0b'}}>{summary.partial}</div>
          </div>
          <div className="card">
            <div className="label">Com falha</div>
            <div className="value err">{summary.fail + summary.error}</div>
          </div>
          <div className="card">
            <div className="label">Total</div>
            <div className="value">{zones.length}</div>
          </div>
        </div>

        {loading && (
          <p style={{color:'#a1a1aa', textAlign:'center', padding:'.5rem'}}>
            Testando zona {Object.keys(results).length + 1}/{zones.length}…
          </p>
        )}

        {/* Por zona */}
        {zones.map(z => {
          const r = results[z.id];
          const allOk = r?.summary?.all_ok;
          const localOk = r?.summary?.local_working;
          const extOk = r?.summary?.external_working;
          return (
            <div key={z.id} style={{marginTop:'.8rem', padding:'.8rem 1rem', border:'1px solid ' + (allOk?'#10b981':localOk?'#f59e0b':'#27272a'), borderRadius:'8px'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div>
                  <strong style={{fontFamily:'JetBrains Mono', fontSize:'1rem'}}>{z.cidr}</strong>
                  <span style={{color:'#a1a1aa', marginLeft:'.5rem', fontSize:'.8rem', fontFamily:'JetBrains Mono'}}>{z.zone_name}</span>
                </div>
                {r === undefined
                  ? <span style={{color:'#a1a1aa'}}>aguardando…</span>
                  : r?.error
                    ? <span className="badge err">erro</span>
                    : allOk
                      ? <span style={{color:'#10b981', display:'inline-flex', alignItems:'center', gap:'.3rem'}}><CheckCircle2 size={16}/> 100% OK</span>
                      : <span style={{color:'#f59e0b', display:'inline-flex', alignItems:'center', gap:'.3rem'}}><AlertTriangle size={16}/> {localOk ? 'só local' : 'falha'}</span>}
              </div>

              {r && !r.error && (
                <div style={{marginTop:'.5rem', display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'.4rem', fontSize:'.78rem'}}>
                  <div style={{padding:'.3rem .5rem', background:'#0a0a0a', borderRadius:'4px'}}>
                    {r.checks.master.ok ? '✓' : '✗'} NS1
                  </div>
                  <div style={{padding:'.3rem .5rem', background:'#0a0a0a', borderRadius:'4px'}}>
                    {r.checks.slave.ok ? '✓' : '✗'} NS2
                  </div>
                  <div style={{padding:'.3rem .5rem', background:'#0a0a0a', borderRadius:'4px'}}>
                    {r.checks.delegated.ok ? '✓' : '✗'} Externo
                  </div>
                  <div style={{padding:'.3rem .5rem', background:'#0a0a0a', borderRadius:'4px'}}>
                    {r.checks.dnssec.ok ? '✓' : '✗'} DNSSEC ({r.checks.dnssec.keys})
                  </div>
                  {r.ptr_count > 0 && (
                    <div style={{gridColumn:'1 / -1', padding:'.3rem .5rem', background:'#0a0a0a', borderRadius:'4px', color:'#a1a1aa'}}>
                      PTRs ({r.ptr_count}): {r.summary.ptrs_local_ok}/{r.ptr_count} local · {r.summary.ptrs_ext_ok}/{r.ptr_count} externo
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="modal-actions">
          <button onClick={runAll} className="secondary" disabled={loading}>
            <RefreshCw size={12}/> {loading ? 'Testando…' : 'Re-testar tudo'}
          </button>
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// Helper: verifica se a subnet está contida no parent (ou é igual)
function isSubnetOrEqual(childCidr, parentCidr) {
  try {
    const [childIp, childLen] = childCidr.split('/');
    const [parentIp, parentLen] = parentCidr.split('/');
    if (parseInt(childLen) < parseInt(parentLen)) return false;
    // IPv4 vs IPv6 detection
    const isV6c = childIp.includes(':');
    const isV6p = parentIp.includes(':');
    if (isV6c !== isV6p) return false;
    // Convert to bigint for comparison
    const toBigInt = (ip, isV6) => {
      if (isV6) {
        // Expand IPv6
        const parts = ip.split('::');
        const head = parts[0] ? parts[0].split(':') : [];
        const tail = parts[1] ? parts[1].split(':') : [];
        const missing = 8 - head.length - tail.length;
        const full = [...head, ...Array(missing).fill('0'), ...tail];
        return full.reduce((acc, p) => (acc << 16n) | BigInt(parseInt(p || '0', 16)), 0n);
      } else {
        return ip.split('.').reduce((acc, o) => (acc << 8n) | BigInt(parseInt(o)), 0n);
      }
    };
    const cBits = toBigInt(childIp, isV6c);
    const pBits = toBigInt(parentIp, isV6p);
    const totalBits = isV6c ? 128 : 32;
    const mask = ((1n << BigInt(totalBits - parseInt(parentLen))) - 1n) ^ ((1n << BigInt(totalBits)) - 1n);
    return (cBits & mask) === (pBits & mask);
  } catch { return false; }
}

function ReverseZoneRow({ z, isOrphan, onDelete, suggestSuffix, onChanged }) {
  const [status, setStatus] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [expanded, setExpanded] = useState(true);  // padrão: aberto, PTRs visíveis
  const [ptrs, setPtrs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [showSubNew, setShowSubNew] = useState(false);
  const [ipInput, setIpInput] = useState('');
  const [ptrInput, setPtrInput] = useState('');
  const [err, setErr] = useState('');

  const isV4    = z.type === 'ipv4';
  const baseIP  = z.cidr.split('/')[0];
  const mask    = parseInt(z.cidr.split('/')[1], 10);

  // Para /24 oferecemos input "last octet" só (203.0.113.X)
  const showShortV4 = isV4 && mask === 24;
  const v4Prefix    = showShortV4 ? baseIP.split('.').slice(0,3).join('.') + '.' : '';

  const loadPtrs = () => api.get(`/dns/reverse/zones/${z.id}/ptr`).then(r => setPtrs(r.data)).catch(()=>setPtrs([]));
  const loadSubs = () => isV4 ? api.get(`/dns/reverse/zones/${z.id}/subdelegations`).then(r => setSubs(r.data)).catch(()=>setSubs([])) : Promise.resolve();

  useEffect(() => {
    api.get(`/dns/reverse/zones/${z.id}/status`).then(r => setStatus(r.data)).catch(() => setStatus({ error: true }));
    loadPtrs(); loadSubs();
  }, [z.id]);

  // Auto-sufixo do hostname: digite "ns1" → vira "ns1.<domain>"
  const normalizePtr = (val) => {
    const v = val.trim().replace(/\.$/, '');
    if (!v) return '';
    if (v.includes('.')) return v;            // já tem domínio
    return suggestSuffix ? `${v}.${suggestSuffix}` : v;
  };

  const add = async () => {
    setErr('');
    const ip  = showShortV4 ? (v4Prefix + ipInput.trim()) : ipInput.trim();
    const ptr = normalizePtr(ptrInput);
    if (!ip || !ptr) { setErr('Preencha IP e hostname'); return; }
    setAdding(true);
    try {
      await api.post(`/dns/reverse/zones/${z.id}/ptr`, { ip, ptr });
      setIpInput(''); setPtrInput('');
      await loadPtrs();
    } catch (e) { setErr(e.response?.data?.error || 'Erro ao adicionar'); }
    finally { setAdding(false); }
  };

  const delPtr = async (id) => {
    if (!window.confirm('Remover este PTR?')) return;
    await api.delete(`/dns/reverse/zones/${z.id}/ptr/${id}`);
    loadPtrs();
  };

  const delSub = async (s) => {
    if (!window.confirm(`Remover sub-delegação ${s.zone_label} (${s.cidr})?`)) return;
    await api.delete(`/dns/reverse/subdelegations/${s.id}`);
    loadSubs();
  };

  const totalCount = ptrs.length + subs.length;

  return (
    <>
      <tr style={isOrphan ? {background:'rgba(245,158,11,.04)'} : {}}>
        <td>
          <button className="ghost" onClick={()=>setExpanded(!expanded)} style={{padding:'.2rem .3rem'}} title={expanded?'Recolher':'Expandir'}>
            {expanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          </button>
        </td>
        <td>
          <strong style={{fontFamily:'JetBrains Mono'}}>{z.cidr}</strong>
          {isOrphan && <span className="badge warn" style={{marginLeft:'.4rem', fontSize:'.65rem'}}>órfã</span>}
          {totalCount > 0 && <span style={{marginLeft:'.4rem', color:'#a1a1aa', fontSize:'.72rem'}}>· {ptrs.length} PTR{subs.length?` · ${subs.length} sub`:''}</span>}
        </td>
        <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>{z.zone_name}</td>
        <td>{status?.local?.responding
          ? <span className="badge ok">ok</span>
          : status ? <span className="badge err">falha</span> : <span style={{color:'#a1a1aa', fontSize:'.78rem'}}>…</span>}</td>
        <td>{status?.external?.delegated
          ? <span className="badge ok">delegado</span>
          : status ? <span className="badge warn">pendente</span> : <span style={{color:'#a1a1aa', fontSize:'.78rem'}}>…</span>}</td>
        <td style={{color:'#a1a1aa'}}>{z.description || '–'}</td>
        <td>
          <button className="secondary" onClick={()=>setShowTest(true)} title="Diagnóstico completo">
            <CheckCircle2 size={12}/> Testar
          </button>{' '}
          <button className="danger" onClick={()=>onDelete(z)} title="Remover zona"><Trash2 size={12}/></button>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td></td>
          <td colSpan={6} style={{background:'#0a0a0c', padding:'.6rem .9rem'}}>
            {/* PTRs existentes */}
            {ptrs.length > 0 ? (
              <div style={{marginBottom:'.6rem'}}>
                {ptrs.map(p => (
                  <div key={p.id} style={{display:'flex', alignItems:'center', gap:'.6rem', padding:'.25rem 0', fontFamily:'JetBrains Mono', fontSize:'.82rem'}}>
                    <span style={{minWidth:'170px', color:'#e4e4e7'}}>{p.ip}</span>
                    <span style={{color:'#a1a1aa'}}>→</span>
                    <span style={{flex:1, color:'#3b82f6'}}>{p.ptr}</span>
                    <button className="danger" onClick={()=>delPtr(p.id)} style={{padding:'.15rem .35rem'}} title="Remover PTR"><Trash2 size={11}/></button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{color:'#52525b', fontSize:'.78rem', marginBottom:'.5rem', fontStyle:'italic'}}>Nenhum PTR cadastrado.</div>
            )}

            {/* Form inline compacto */}
            <div style={{display:'flex', gap:'.4rem', alignItems:'center', flexWrap:'wrap'}}>
              {showShortV4 && (
                <span style={{fontFamily:'JetBrains Mono', color:'#a1a1aa', fontSize:'.82rem'}}>{v4Prefix}</span>
              )}
              <input
                value={ipInput}
                onChange={e=>setIpInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter' && add()}
                placeholder={showShortV4 ? '10' : (isV4 ? '203.0.113.10' : '2001:db8::10')}
                style={{fontFamily:'JetBrains Mono', width: showShortV4 ? '80px' : '220px', padding:'.35rem .5rem', fontSize:'.82rem'}} />
              <span style={{color:'#a1a1aa'}}>→</span>
              <input
                value={ptrInput}
                onChange={e=>setPtrInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter' && add()}
                placeholder={`ns1 ou ns1.${suggestSuffix||'exemplo.com'}`}
                style={{fontFamily:'JetBrains Mono', flex:'1 1 220px', padding:'.35rem .5rem', fontSize:'.82rem'}} />
              <button onClick={add} disabled={adding || !ipInput || !ptrInput} style={{padding:'.35rem .8rem', fontSize:'.82rem'}}>
                <Plus size={12}/> Adicionar PTR
              </button>
            </div>
            {err && <div style={{color:'#ef4444', fontSize:'.78rem', marginTop:'.4rem'}}>{err}</div>}
            {ptrInput && !ptrInput.includes('.') && suggestSuffix && (
              <div style={{color:'#52525b', fontSize:'.72rem', marginTop:'.3rem'}}>
                Será gravado como <code>{normalizePtr(ptrInput)}</code>
              </div>
            )}

            {/* Sub-delegações (apenas v4) */}
            {isV4 && (
              <div style={{marginTop:'.9rem', paddingTop:'.6rem', borderTop:'1px solid #1f1f23'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.3rem'}}>
                  <strong style={{fontSize:'.82rem', color:'#a1a1aa'}}>Sub-delegações (RFC 2317)</strong>
                  <button className="secondary" onClick={()=>setShowSubNew(true)} style={{padding:'.2rem .5rem', fontSize:'.75rem'}}>
                    <Plus size={11}/> Nova sub-delegação
                  </button>
                </div>
                {subs.length === 0 ? (
                  <div style={{color:'#52525b', fontSize:'.75rem', fontStyle:'italic'}}>Nenhuma sub-delegação.</div>
                ) : (
                  <div style={{display:'flex', flexDirection:'column', gap:'.3rem'}}>
                    {subs.map(s => (
                      <SubdelegationInlineRow key={s.id} sub={s} suggestSuffix={suggestSuffix} onDelete={()=>delSub(s)} onChanged={loadSubs} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}

      {showTest && <ReverseTestModal zone={z} onClose={()=>setShowTest(false)} />}
      {showSubNew && <NewSubdelegationModal zone={z} onClose={()=>{ setShowSubNew(false); loadSubs(); }} />}
    </>
  );
}

function SubdelegationInlineRow({ sub, suggestSuffix, onDelete, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [ptrs, setPtrs] = useState([]);
  const [ipInput, setIpInput] = useState('');
  const [ptrInput, setPtrInput] = useState('');
  const [err, setErr] = useState('');

  const isLocal = sub.mode === 'local';
  const loadPtrs = () => isLocal ? api.get(`/dns/reverse/subdelegations/${sub.id}/ptr`).then(r => setPtrs(r.data)).catch(()=>setPtrs([])) : Promise.resolve();
  useEffect(() => { if (expanded && isLocal) loadPtrs(); }, [expanded]);

  const normalizePtr = (val) => {
    const v = val.trim().replace(/\.$/, '');
    if (!v) return '';
    if (v.includes('.')) return v;
    return suggestSuffix ? `${v}.${suggestSuffix}` : v;
  };

  const add = async () => {
    setErr('');
    const ip  = ipInput.trim();
    const ptr = normalizePtr(ptrInput);
    if (!ip || !ptr) { setErr('Preencha IP e hostname'); return; }
    try {
      await api.post(`/dns/reverse/subdelegations/${sub.id}/ptr`, { ip, ptr });
      setIpInput(''); setPtrInput(''); loadPtrs();
    } catch (e) { setErr(e.response?.data?.error || 'Erro'); }
  };

  const delPtr = async (id) => {
    if (!window.confirm('Remover este PTR?')) return;
    await api.delete(`/dns/reverse/subdelegations/${sub.id}/ptr/${id}`);
    loadPtrs();
  };

  return (
    <div style={{background:'#1a1a1d', borderRadius:'4px', padding:'.4rem .6rem'}}>
      <div style={{display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.8rem'}}>
        {isLocal && (
          <button className="ghost" onClick={()=>setExpanded(!expanded)} style={{padding:'.1rem .2rem'}}>
            <ChevronDown size={12} style={{transform: expanded?'none':'rotate(-90deg)'}}/>
          </button>
        )}
        <strong style={{fontFamily:'JetBrains Mono'}}>{sub.cidr}</strong>
        <span style={{color:'#a1a1aa'}}>·</span>
        <span style={{fontFamily:'JetBrains Mono', fontSize:'.72rem', color:'#a1a1aa', flex:1}}>{sub.zone_name}</span>
        {isLocal ? <span className="badge ok" style={{fontSize:'.65rem'}}>local</span> : <span className="badge warn" style={{fontSize:'.65rem'}}>delegada → {sub.external_ns}</span>}
        <button className="danger" onClick={onDelete} style={{padding:'.15rem .3rem'}}><Trash2 size={11}/></button>
      </div>

      {expanded && isLocal && (
        <div style={{marginTop:'.4rem', paddingLeft:'1.5rem'}}>
          {ptrs.map(p => (
            <div key={p.id} style={{display:'flex', alignItems:'center', gap:'.5rem', padding:'.15rem 0', fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>
              <span style={{minWidth:'140px'}}>{p.ip}</span>
              <span style={{color:'#a1a1aa'}}>→</span>
              <span style={{flex:1, color:'#3b82f6'}}>{p.ptr}</span>
              <button className="danger" onClick={()=>delPtr(p.id)} style={{padding:'.1rem .25rem'}}><Trash2 size={10}/></button>
            </div>
          ))}
          {ptrs.length === 0 && <div style={{color:'#52525b', fontSize:'.72rem', fontStyle:'italic'}}>Sem PTRs nesta sub-delegação.</div>}
          <div style={{display:'flex', gap:'.3rem', marginTop:'.3rem', alignItems:'center', flexWrap:'wrap'}}>
            <input value={ipInput} onChange={e=>setIpInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&add()}
              placeholder="203.0.113.x" style={{fontFamily:'JetBrains Mono', width:'160px', padding:'.25rem .4rem', fontSize:'.75rem'}}/>
            <span style={{color:'#a1a1aa'}}>→</span>
            <input value={ptrInput} onChange={e=>setPtrInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&add()}
              placeholder={`host ou host.${suggestSuffix||'ex.com'}`} style={{fontFamily:'JetBrains Mono', flex:'1 1 180px', padding:'.25rem .4rem', fontSize:'.75rem'}}/>
            <button onClick={add} disabled={!ipInput||!ptrInput} style={{padding:'.25rem .5rem', fontSize:'.75rem'}}><Plus size={11}/></button>
          </div>
          {err && <div style={{color:'#ef4444', fontSize:'.72rem', marginTop:'.2rem'}}>{err}</div>}
        </div>
      )}
    </div>
  );
}

function ReverseTestModal({ zone, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setErr(''); setData(null);
    try {
      const r = await api.get(`/dns/reverse/zones/${zone.id}/test`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Erro'); }
    setLoading(false);
  };
  useEffect(() => { run(); }, [zone.id]);

  const Item = ({ ok, label, detail }) => (
    <div style={{display:'flex', alignItems:'center', gap:'.6rem', padding:'.5rem 0', borderBottom:'1px solid #1f1f23'}}>
      <span style={{width:'24px'}}>{ok === null ? '…' : ok ? <CheckCircle2 size={18} color="#10b981"/> : <XCircle size={18} color="#ef4444"/>}</span>
      <div style={{flex:1}}>
        <div style={{fontWeight:500}}>{label}</div>
        {detail && <div style={{color:'#a1a1aa', fontSize:'.78rem', fontFamily:'JetBrains Mono'}}>{detail}</div>}
      </div>
    </div>
  );

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'620px', maxWidth:'90vw'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <h2 style={{margin:0}}>Diagnóstico — {zone.cidr}</h2>
          <button onClick={onClose} className="ghost"><X size={18}/></button>
        </div>
        <p style={{color:'#a1a1aa', fontSize:'.82rem', marginTop:'.3rem'}}>
          Zona: <code>{zone.zone_name}</code>
        </p>

        {loading && <p style={{color:'#a1a1aa'}}>Executando testes…</p>}
        {err && <div className="err-msg">{err}</div>}

        {data && (
          <>
            {/* Resumo — interpretação por camadas (BIND → delegação → PTR local → PTR externo) */}
            {(() => {
              const bindOk     = data.checks.master.ok && data.checks.slave.ok;
              const delegated  = data.checks.delegated.ok;
              const ptrLocalOk = data.ptr_count === 0 || data.summary.ptrs_local_ok === data.ptr_count;
              const ptrExtOk   = data.ptr_count === 0 || data.summary.ptrs_ext_ok   === data.ptr_count;
              const allGreen   = bindOk && delegated && ptrLocalOk && ptrExtOk;

              let color, title, hint;
              if (allGreen) {
                color = '#10b981'; title = '✓ Reverso 100% funcional';
                hint  = 'Zona delegada, BIND9 servindo, PTRs resolvendo externamente.';
              } else if (!bindOk) {
                color = '#ef4444'; title = '✗ BIND9 não está respondendo';
                hint  = 'Master ou slave fora do ar. Verifique containers tguth_bind9_master / tguth_bind9_slave (Sistema → Serviços Docker).';
              } else if (!delegated) {
                color = '#f59e0b'; title = '⚠ Funciona localmente, falta delegação externa';
                hint  = 'Configure no Registro.br / provedor do bloco IP para delegar a zona aos seus NS.';
              } else if (!ptrLocalOk) {
                color = '#f59e0b'; title = '⚠ Servidores e delegação OK, mas o PTR aponta para outro lugar';
                hint  = 'O PTR pode estar via sub-delegação RFC 2317 (CNAME → zona delegada). Se for o caso, é esperado e está correto.';
              } else if (!ptrExtOk) {
                color = '#f59e0b'; title = '⚠ PTR local OK, ainda propagando externamente';
                hint  = 'Cache do resolver externo (1.1.1.1) ainda não tem. Aguarde alguns minutos.';
              }

              return (
                <div style={{padding:'.8rem 1rem',
                  background: color === '#10b981' ? 'rgba(16,185,129,.1)' : color === '#ef4444' ? 'rgba(239,68,68,.1)' : 'rgba(245,158,11,.1)',
                  border:'1px solid ' + color, borderRadius:'8px', marginTop:'.5rem', marginBottom:'1rem', fontSize:'.9rem'}}>
                  <strong style={{color}}>{title}</strong>
                  <p style={{margin:'.3rem 0 0', color:'#a1a1aa', fontSize:'.82rem'}}>{hint}</p>
                </div>
              );
            })()}

            <h3 style={{margin:'.5rem 0', fontSize:'.95rem'}}>Servidores DNS</h3>
            <Item ok={data.checks.master.ok}
              label="NS1 (master) respondendo"
              detail={data.checks.master.ns?.join(', ') || 'sem resposta'} />
            <Item ok={data.checks.slave.ok}
              label="NS2 (slave) respondendo"
              detail={data.checks.slave.ns?.join(', ') || 'sem resposta'} />
            <Item ok={data.checks.delegated.ok}
              label="Delegação externa (visível na internet)"
              detail={data.checks.delegated.ns?.join(', ') || 'Registro.br/LACNIC ainda não delegou para seus NS'} />
            <Item ok={data.checks.dnssec.ok}
              label="DNSSEC (DNSKEY publicada)"
              detail={`${data.checks.dnssec.keys || 0} chave(s)`} />

            {data.ptr_count > 0 && (
              <>
                <h3 style={{margin:'1rem 0 .5rem', fontSize:'.95rem'}}>PTRs (amostra de até 3)</h3>
                {data.ptr_tests.map((p, i) => (
                  <div key={i} style={{padding:'.6rem', background:'#0a0a0a', borderRadius:'6px', marginBottom:'.5rem', fontSize:'.82rem'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.3rem'}}>
                      <code style={{color:'#3b82f6'}}>{p.ip} → {p.expected}</code>
                      <div style={{display:'flex', gap:'.6rem'}}>
                        <span style={{display:'inline-flex', alignItems:'center', gap:'.2rem', color: p.local_ok ? '#10b981' : '#ef4444'}}>
                          {p.local_ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>} Local
                        </span>
                        <span style={{display:'inline-flex', alignItems:'center', gap:'.2rem', color: p.external_ok ? '#10b981' : '#f59e0b'}}>
                          {p.external_ok ? <CheckCircle2 size={14}/> : <AlertTriangle size={14}/>} Externo
                        </span>
                      </div>
                    </div>
                    <div style={{color:'#a1a1aa', fontSize:'.78rem'}}>
                      Local (NS1):  <code>{p.local_answer}</code>
                    </div>
                    <div style={{color:'#a1a1aa', fontSize:'.78rem'}}>
                      Externo (1.1.1.1): <code>{p.external_answer}</code>
                    </div>
                  </div>
                ))}
              </>
            )}

            {data.ptr_count === 0 && (
              <div style={{padding:'.6rem 1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'6px', fontSize:'.82rem', marginTop:'.5rem'}}>
                ⚠ Nenhum PTR cadastrado nesta zona. Use "Gerenciar PTRs" para adicionar.
              </div>
            )}
          </>
        )}

        <div className="modal-actions">
          <button onClick={run} className="secondary" disabled={loading}>
            <RefreshCw size={12}/> {loading ? 'Testando…' : 'Re-testar'}
          </button>
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

function NewSubdelegationModal({ zone, onClose }) {
  const [form, setForm] = useState({ cidr: '', zone_label: '', mode: 'local', external_ns: '', description: '' });
  const [msg, setMsg] = useState('');

  const create = async () => {
    setMsg('Criando…');
    try {
      await api.post(`/dns/reverse/zones/${zone.id}/subdelegations`, form);
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  const parentRange = zone.cidr.split('/')[0].split('.').slice(0,3).join('.');

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'520px'}}>
        <h2>Nova Sub-delegação — dentro de {zone.cidr}</h2>
        <label>CIDR do sub-bloco</label>
        <input value={form.cidr} onChange={e=>setForm({...form, cidr: e.target.value})}
          placeholder={`${parentRange}.128/25 ou ${parentRange}.64/26`} style={{fontFamily:'JetBrains Mono'}} />
        <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
          Aceita /25, /26, /27, /28, /29, /30, /31, /32 — deve estar contido em <code>{zone.cidr}</code>
        </p>

        <label>Nome (label da sub-zona)</label>
        <input value={form.zone_label} onChange={e=>setForm({...form, zone_label: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-')})}
          placeholder="clientes ou dedicado-x" style={{fontFamily:'JetBrains Mono'}} />
        <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
          Sub-zona vai chamar: <code>{form.zone_label ? `${form.zone_label}.${zone.zone_name}` : `<nome>.${zone.zone_name}`}</code>
        </p>

        <label>Modo</label>
        <div style={{display:'flex', gap:'.6rem', marginTop:'.2rem'}}>
          <label style={{flex:1, padding:'.6rem', border:'1px solid ' + (form.mode==='local'?'#3b82f6':'#27272a'), borderRadius:'6px', cursor:'pointer'}}>
            <input type="radio" style={{width:'auto', marginRight:'.4rem'}} checked={form.mode==='local'} onChange={()=>setForm({...form, mode:'local'})} />
            <strong>Local</strong>
            <div style={{color:'#a1a1aa', fontSize:'.78rem'}}>PTRs gerenciados aqui</div>
          </label>
          <label style={{flex:1, padding:'.6rem', border:'1px solid ' + (form.mode==='delegated'?'#3b82f6':'#27272a'), borderRadius:'6px', cursor:'pointer'}}>
            <input type="radio" style={{width:'auto', marginRight:'.4rem'}} checked={form.mode==='delegated'} onChange={()=>setForm({...form, mode:'delegated'})} />
            <strong>Delegado</strong>
            <div style={{color:'#a1a1aa', fontSize:'.78rem'}}>Aponta para NS externo</div>
          </label>
        </div>

        {form.mode === 'delegated' && (
          <>
            <label>NS externos (separados por vírgula)</label>
            <input value={form.external_ns} onChange={e=>setForm({...form, external_ns: e.target.value})}
              placeholder="ns1.cliente.com.br, ns2.cliente.com.br" style={{fontFamily:'JetBrains Mono'}} />
          </>
        )}

        <label>Descrição (opcional)</label>
        <input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
          placeholder="Ex: clientes residenciais" />

        {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro') ? '#ef4444':'#10b981'}}>{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={create} disabled={!form.cidr || !form.zone_label || (form.mode==='delegated' && !form.external_ns)}>Criar</button>
        </div>
      </div>
    </div>
  );
}


// ── DNSSEC ──────────────────────────────────────────────────────
function Dnssec({ domain }) {
  const [ds, setDs] = useState('Carregando…');
  useEffect(() => {
    api.get(`/dns/${domain.name}/dnssec`).then(r => setDs(r.data.dsRecord || '(aguarde geração das chaves)'))
       .catch(e => setDs(e.response?.data?.error || 'Erro'));
  }, [domain.name]);

  const copy = () => navigator.clipboard.writeText(ds);
  const rollover = async () => {
    if (!window.confirm('Rollover de chaves DNSSEC?')) return;
    try { const r = await api.post(`/dns/${domain.name}/dnssec/rollover`); alert(r.data.output); }
    catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  if (!domain.has_dnssec) return <p style={{color:'#a1a1aa'}}>DNSSEC não está ativo para este domínio.</p>;

  return (
    <>
      <h3>Registro DS</h3>
      <p style={{color:'#a1a1aa', fontSize:'.88rem'}}>Cole este registro no painel do seu registrador, na seção DNSSEC.</p>
      <pre className="code">{ds}</pre>
      <div style={{marginTop:'.6rem'}}>
        <button onClick={copy} className="secondary"><Copy size={12}/> Copiar</button>{' '}
        <button onClick={rollover} className="ghost"><RefreshCw size={12}/> Rollover</button>
      </div>
    </>
  );
}

// ── RPKI por domínio ────────────────────────────────────────────
const RPKI_STATE = {
  'valid':     { color: '#10b981', icon: CheckCircle2,  label: 'VÁLIDO',         hint: 'Existe ROA que cobre este ASN+prefixo. Sequestros BGP serão rejeitados por ISPs com RPKI.' },
  'invalid':   { color: '#ef4444', icon: XCircle,       label: 'INVÁLIDO',       hint: 'Há ROA, mas o ASN ou tamanho do prefixo não bate. Risco de ter rotas rejeitadas!' },
  'not-found': { color: '#f59e0b', icon: AlertTriangle, label: 'NÃO ENCONTRADO', hint: 'Sem ROA publicada — anúncio aceito mas sequestros também passam. Crie uma ROA no NIC.br.' },
};

function RpkiTab({ domain }) {
  const [selectedAsn, setSelectedAsn] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (selectedAsn) {
    return <RpkiAsnDetail
      domain={domain}
      asn={selectedAsn}
      onBack={() => { setSelectedAsn(null); setRefreshKey(k => k+1); }}
    />;
  }
  return <RpkiAsnList domain={domain} key={refreshKey} onSelect={setSelectedAsn} />;
}

// ─── LIST: ASNs do domínio (cards: Domínio | Organização | Prefixos) ───
function RpkiAsnList({ domain, onSelect }) {
  const [asns, setAsns] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/domains/${domain.name}/asns`);
      setAsns(r.data);
    } catch (e) { setAsns([]); }
  };
  useEffect(() => { load(); }, [domain.name]);

  if (asns === null) return <p style={{color:'#a1a1aa'}}>Carregando…</p>;

  return (
    <>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <h3 style={{margin:0}}>RPKI · ASNs vinculados</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
            Cada ASN tem sua própria CA (Krill) e fluxo de configuração isolado.
            Um domínio pode ter múltiplos ASNs.
          </p>
        </div>
        <div style={{display:'flex', gap:'.4rem'}}>
          <button onClick={() => setShowLink(true)} className="secondary">Vincular ASN existente</button>
          <button onClick={() => setShowAdd(true)}><Plus size={14}/> Novo ASN</button>
        </div>
      </div>

      {asns.length === 0 ? (
        <div style={{padding:'1.5rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', marginTop:'1rem'}}>
          <strong style={{color:'#f59e0b'}}>Nenhum ASN vinculado</strong>
          <p style={{margin:'.4rem 0 0', color:'#a1a1aa', fontSize:'.88rem'}}>
            Adicione um novo ASN ou vincule um já cadastrado para começar a gerenciar ROAs via RPKI Delegado.
          </p>
        </div>
      ) : (
        <table style={{marginTop:'1rem'}}>
          <thead>
            <tr><th>Domínio</th><th>Organização</th><th>ASN</th><th>Prefixos</th><th></th></tr>
          </thead>
          <tbody>
            {asns.map(a => (
              <tr key={a.id} onClick={() => onSelect(a)} style={{cursor:'pointer'}}>
                <td><strong>{domain.name}</strong></td>
                <td>{a.org_name || '–'}</td>
                <td style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</td>
                <td>
                  {(a.prefixes || []).map(p => (
                    <span key={p.id} className="badge ok" style={{marginRight:'.3rem', fontFamily:'JetBrains Mono'}}>{p.prefix}</span>
                  ))}
                  {!a.prefixes?.length && <span style={{color:'#a1a1aa'}}>sem prefixos</span>}
                </td>
                <td><button className="secondary"><Search size={12}/> Configurar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && <NewAsnModal domain={domain} onClose={()=>{ setShowAdd(false); load(); }} />}
      {showLink && <LinkAsnModal domain={domain} alreadyIds={asns.map(a=>a.id)} onClose={()=>{ setShowLink(false); load(); }} />}
    </>
  );
}

function NewAsnModal({ domain, onClose }) {
  const [form, setForm] = useState({ asn: '', org_name: '', description: '', prefix: '', max_length: '' });
  const [msg, setMsg] = useState('');

  const save = async () => {
    setMsg('Criando ASN e CA…');
    try {
      const r = await api.post('/asn', {
        asn: form.asn, org_name: form.org_name || null, description: form.description || null,
      });
      const asnId = r.data.id;
      if (form.prefix) {
        await api.post(`/asn/${asnId}/prefix`, {
          prefix: form.prefix,
          max_length: form.max_length || null,
          description: 'Cadastrado junto ao ASN',
        });
      }
      // Vincular ao domínio
      const cur = await api.get(`/domains/${domain.name}/asns`);
      const ids = cur.data.map(a => a.id);
      await api.post(`/domains/${domain.name}/asns`, { asnIds: [...ids, asnId] });
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>Novo ASN para {domain.name}</h2>
        <p style={{color:'#a1a1aa', fontSize:'.82rem'}}>
          Vai criar o ASN, vincular ao domínio e <strong>auto-criar uma CA no Krill</strong> para esse ASN.
        </p>
        <label>ASN</label>
        <input value={form.asn} onChange={e=>setForm({...form, asn: e.target.value})}
          placeholder="AS65000" style={{fontFamily:'JetBrains Mono'}} />
        <label>Organização</label>
        <input value={form.org_name} onChange={e=>setForm({...form, org_name: e.target.value})}
          placeholder="Hiperlink Telecom LTDA" />
        <label>Descrição (opcional)</label>
        <input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
          placeholder="AS principal / cliente X / etc" />
        <h4 style={{marginTop:'1rem'}}>Adicionar primeiro prefixo (opcional)</h4>
        <div className="row">
          <input value={form.prefix} onChange={e=>setForm({...form, prefix: e.target.value})}
            placeholder="203.0.113.0/24 ou 2001:db8::/32" style={{fontFamily:'JetBrains Mono'}} />
          <div style={{maxWidth:'120px'}}>
            <input value={form.max_length} onChange={e=>setForm({...form, max_length: e.target.value})}
              type="number" placeholder="MaxLen" />
          </div>
        </div>
        {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro')?'#ef4444':'#a1a1aa'}}>{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save} disabled={!form.asn}><Save size={12}/> Criar e vincular</button>
        </div>
      </div>
    </div>
  );
}

function LinkAsnModal({ domain, alreadyIds, onClose }) {
  const [allAsns, setAllAsns] = useState([]);
  const [selected, setSelected] = useState(alreadyIds);
  useEffect(() => { api.get('/asn').then(r => setAllAsns(r.data)); }, []);
  const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const save = async () => {
    await api.post(`/domains/${domain.name}/asns`, { asnIds: selected });
    onClose();
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'500px'}}>
        <h2>Vincular ASNs a {domain.name}</h2>
        <div style={{maxHeight:'320px', overflow:'auto', border:'1px solid #27272a', borderRadius:'6px', padding:'.5rem'}}>
          {allAsns.length === 0 ? <p style={{color:'#a1a1aa', margin:0}}>Nenhum ASN cadastrado ainda.</p>
            : allAsns.map(a => (
              <label key={a.id} style={{display:'flex', alignItems:'center', gap:'.5rem', padding:'.4rem 0', cursor:'pointer'}}>
                <input type="checkbox" style={{width:'auto'}} checked={selected.includes(a.id)} onChange={()=>toggle(a.id)} />
                <strong style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</strong>
                {a.org_name && <span style={{color:'#a1a1aa'}}>{a.org_name}</span>}
              </label>
            ))}
        </div>
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ─── DETAIL: ASN selecionado, configuração isolada ───────────────
function RpkiAsnDetail({ domain, asn, onBack }) {
  const caHandle = asn.ca_handle;
  const [wizard, setWizard] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [validity, setValidity] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const [parentXml, setParentXml] = useState('');
  const [parentHandle, setParentHandle] = useState('registro-br');
  const [repoXml, setRepoXml] = useState('');

  const [newRoa, setNewRoa] = useState({ prefix: '', max_length: '' });

  const load = async () => {
    if (!caHandle) return;
    setLoading(true);
    try {
      const [w, r] = await Promise.allSettled([
        api.get(`/krill/cas/${caHandle}/wizard`),
        api.get(`/krill/cas/${caHandle}/routes`),
      ]);
      if (w.status === 'fulfilled') setWizard(w.value.data);
      if (r.status === 'fulfilled') setRoutes(r.value.data || []);

      // Valida cada prefixo do ASN no Routinator
      const validityResults = {};
      for (const p of (asn.prefixes || [])) {
        try {
          const v = await api.get(`/rpki/validity?asn=${asn.asn}&prefix=${encodeURIComponent(p.prefix)}`);
          validityResults[p.prefix] = v.data.validated_route?.validity?.state || 'unknown';
        } catch {}
      }
      setValidity(validityResults);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [asn.id, caHandle]);

  const download = (filename, content) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  };

  const submitParent = async () => {
    setMsg('Enviando parent_response…');
    try {
      await api.post(`/krill/cas/${caHandle}/parent`, { parentHandle, response: parentXml });
      setMsg('✓ Parent registrado');
      setParentXml('');
      load();
    } catch (e) { setMsg(JSON.stringify(e.response?.data?.error || e.message)); }
  };

  const submitRepo = async () => {
    setMsg('Enviando repository_response…');
    try {
      await api.post(`/krill/cas/${caHandle}/repo`, { response: repoXml });
      setMsg('✓ Repositório configurado');
      setRepoXml('');
      load();
    } catch (e) { setMsg(JSON.stringify(e.response?.data?.error || e.message)); }
  };

  const addRoute = async (prefix, maxLength) => {
    setMsg('Criando ROA…');
    try {
      const asNum = parseInt(asn.asn.replace(/^AS/i, ''), 10);
      await api.post(`/krill/cas/${caHandle}/routes`, {
        added: [{
          asn: asNum,
          prefix,
          max_length: parseInt(maxLength, 10),
        }],
      });
      setMsg('✓ ROA criada (propagação ~30 min)');
      setNewRoa({ prefix: '', max_length: '' });
      load();
    } catch (e) { setMsg(JSON.stringify(e.response?.data?.error || e.message)); }
  };

  const removeRoute = async (r) => {
    if (!window.confirm(`Remover ROA AS${r.asn} ${r.prefix}?`)) return;
    await api.post(`/krill/cas/${caHandle}/routes`, {
      removed: [{ asn: r.asn, prefix: r.prefix, max_length: r.max_length }],
    });
    load();
  };

  if (!caHandle) return (
    <>
      <button onClick={onBack} className="secondary"><ArrowLeft size={14}/> Voltar</button>
      <p style={{color:'#ef4444', marginTop:'1rem'}}>Este ASN não tem CA associada. Recadastre.</p>
    </>
  );

  const step1 = !!wizard?.child_request;
  const step2 = wizard?.has_parent;
  const step3 = !!wizard?.publisher_request;
  const step4 = wizard?.has_repo;
  const allDone = step1 && step2 && step3 && step4;

  const myAsNum = parseInt(asn.asn.replace(/^AS/i, ''), 10);
  const myRoutes = routes.filter(r => r.asn === myAsNum);

  return (
    <>
      <button onClick={onBack} className="secondary" style={{marginBottom:'1rem'}}>
        <ArrowLeft size={14}/> Voltar para lista de ASNs
      </button>

      {/* Header */}
      <div style={{padding:'1.2rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            <div style={{color:'#a1a1aa', fontSize:'.78rem', textTransform:'uppercase', letterSpacing:'.1em'}}>{domain.name}</div>
            <div style={{fontFamily:'JetBrains Mono', color:'#3b82f6', fontSize:'1.5rem', fontWeight:'700'}}>{asn.asn}</div>
            <div style={{color:'#a1a1aa', marginTop:'.2rem'}}>{asn.org_name || 'Sem organização'}</div>
            <div style={{marginTop:'.5rem'}}>
              {(asn.prefixes || []).map(p => {
                const state = validity[p.prefix];
                const meta = RPKI_STATE[state] || null;
                return (
                  <span key={p.id} style={{display:'inline-block', marginRight:'.4rem', marginBottom:'.3rem', padding:'.2rem .5rem', background:'#0a0a0a', border:'1px solid #27272a', borderRadius:'4px', fontFamily:'JetBrains Mono', fontSize:'.8rem'}}>
                    {p.prefix}{p.max_length && p.max_length !== parseInt(p.prefix.split('/')[1], 10) ? ` (maxLen ${p.max_length})` : ''}
                    {meta && <span style={{marginLeft:'.4rem', color: meta.color, fontSize:'.72rem'}}>● {meta.label}</span>}
                  </span>
                );
              })}
            </div>
          </div>
          <div>
            <span className={'badge ' + (allDone ? 'ok' : 'warn')} style={{fontSize:'.85rem', padding:'.4rem .8rem'}}>
              {allDone ? '✓ CA totalmente configurada' : 'Configuração pendente'}
            </span>
          </div>
        </div>
      </div>

      {/* Wizard steps */}
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'.4rem', marginTop:'1.5rem'}}>
        <StepCard n={1} ok={step1} title="Child Request" hint="Gerar XML"/>
        <StepCard n={2} ok={step2} title="Parent Response" hint="Receber do Registro.br"/>
        <StepCard n={3} ok={step3} title="Publisher Request" hint="Gerar XML"/>
        <StepCard n={4} ok={step4} title="Repository Response" hint="Receber do Registro.br"/>
      </div>

      {/* STEP 1: Child Request */}
      <Section n="1" title="Child Request — Identidade" done={step1}>
        <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
          Baixe este XML e submeta em <a href="https://registro.br/tecnologia/numeracao/rpki/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>registro.br → RPKI Delegado <ExternalLink size={10}/></a>
          informando o ASN <code>{asn.asn}</code>.
        </p>
        <div style={{display:'flex', gap:'.4rem'}}>
          <button onClick={() => download(`child_request_${caHandle}.xml`, wizard?.child_request || '')} disabled={!step1}>
            <Download size={12}/> Baixar child_request.xml
          </button>
          <button onClick={() => { navigator.clipboard.writeText(wizard?.child_request || ''); setMsg('Copiado'); }} className="secondary" disabled={!step1}>
            <Copy size={12}/> Copiar
          </button>
        </div>
        {step1 && (
          <pre className="code" style={{maxHeight:'200px', overflow:'auto', fontSize:'.7rem', marginTop:'.6rem'}}>{wizard.child_request}</pre>
        )}
      </Section>

      {/* STEP 2: Parent Response */}
      <Section n="2" title="Parent Response — cole o XML retornado" done={step2}>
        {step2 ? (
          <div style={{padding:'.6rem 1rem', background:'rgba(16,185,129,.1)', border:'1px solid #10b981', borderRadius:'6px'}}>
            ✓ Parent registrado: {wizard?.parents?.map((p, i) => {
              const name = typeof p === 'string' ? p : (p?.handle || p?.parent_handle || 'configurado');
              const kind = typeof p === 'object' ? p?.kind : null;
              return <span key={i} style={{marginRight:'.5rem'}}>
                <strong>{name}</strong>{kind ? ` (${kind})` : ''}
              </span>;
            })}
          </div>
        ) : (
          <>
            <label>Parent Handle</label>
            <input value={parentHandle} onChange={e=>setParentHandle(e.target.value)} placeholder="registro-br" style={{maxWidth:'250px'}} />
            <label>Parent Response XML</label>
            <textarea value={parentXml} onChange={e=>setParentXml(e.target.value)}
              placeholder="<parent_response ...>...</parent_response>"
              style={{minHeight:'160px', fontFamily:'JetBrains Mono', fontSize:'.78rem'}} />
            <div style={{marginTop:'.5rem'}}>
              <button onClick={submitParent} disabled={!parentXml}><Upload size={12}/> Enviar</button>
            </div>
          </>
        )}
      </Section>

      {/* STEP 3: Publisher Request */}
      <Section n="3" title="Publisher Request — Identidade do publicador" done={step3}>
        <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
          Após o parent_response ser aceito, baixe este XML e submeta novamente no Registro.br para configurar o repositório.
        </p>
        <div style={{display:'flex', gap:'.4rem'}}>
          <button onClick={() => download(`publisher_request_${caHandle}.xml`, wizard?.publisher_request || '')} disabled={!step3}>
            <Download size={12}/> Baixar publisher_request.xml
          </button>
          <button onClick={() => { navigator.clipboard.writeText(wizard?.publisher_request || ''); setMsg('Copiado'); }} className="secondary" disabled={!step3}>
            <Copy size={12}/> Copiar
          </button>
        </div>
        {step3 && (
          <pre className="code" style={{maxHeight:'200px', overflow:'auto', fontSize:'.7rem', marginTop:'.6rem'}}>{wizard.publisher_request}</pre>
        )}
      </Section>

      {/* STEP 4: Repository Response */}
      <Section n="4" title="Repository Response — cole o XML do repositório" done={step4}>
        {step4 ? (
          <div style={{padding:'.6rem 1rem', background:'rgba(16,185,129,.1)', border:'1px solid #10b981', borderRadius:'6px'}}>
            ✓ Repositório configurado
          </div>
        ) : (
          <>
            <label>Repository Response XML</label>
            <textarea value={repoXml} onChange={e=>setRepoXml(e.target.value)}
              placeholder="<repository_response ...>...</repository_response>"
              style={{minHeight:'160px', fontFamily:'JetBrains Mono', fontSize:'.78rem'}} />
            <div style={{marginTop:'.5rem'}}>
              <button onClick={submitRepo} disabled={!repoXml}><Upload size={12}/> Enviar</button>
            </div>
          </>
        )}
      </Section>

      {/* ROAs */}
      <Section n="✓" title={`ROAs deste ASN (${myRoutes.length})`} done={myRoutes.length > 0} active={allDone}>
        {!allDone ? (
          <p style={{color:'#a1a1aa'}}>Complete as 4 etapas acima antes de criar ROAs.</p>
        ) : (
          <>
            <table>
              <thead><tr><th>ASN</th><th>Prefixo</th><th>Max Length</th><th></th></tr></thead>
              <tbody>
                {myRoutes.length === 0
                  ? <tr><td colSpan={4} style={{color:'#a1a1aa'}}>Sem ROAs. Use sugestões abaixo ou o form.</td></tr>
                  : myRoutes.map((r, i) => (
                    <tr key={i}>
                      <td style={{fontFamily:'JetBrains Mono'}}>AS{r.asn}</td>
                      <td style={{fontFamily:'JetBrains Mono'}}>{r.prefix}</td>
                      <td>{r.max_length}</td>
                      <td><button className="danger" onClick={()=>removeRoute(r)}><Trash2 size={12}/></button></td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Sugestões automáticas dos prefixos do ASN */}
            {(asn.prefixes || []).filter(p => !myRoutes.some(r => r.prefix === p.prefix)).length > 0 && (
              <>
                <h4 style={{marginTop:'1rem', color:'#3b82f6', fontSize:'.9rem'}}>💡 Criar ROAs dos prefixos cadastrados:</h4>
                {asn.prefixes.filter(p => !myRoutes.some(r => r.prefix === p.prefix)).map(p => (
                  <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.4rem 0'}}>
                    <code style={{fontSize:'.85rem'}}>{asn.asn} → {p.prefix} maxLen={p.max_length}</code>
                    <button onClick={()=>addRoute(p.prefix, p.max_length)}><Plus size={12}/> Criar ROA</button>
                  </div>
                ))}
              </>
            )}

            <h4 style={{marginTop:'1rem'}}>Adicionar ROA manualmente</h4>
            <div className="row">
              <input value={newRoa.prefix} onChange={e=>setNewRoa({...newRoa, prefix: e.target.value})} placeholder="203.0.113.0/24" style={{fontFamily:'JetBrains Mono'}} />
              <div style={{maxWidth:'120px'}}>
                <input value={newRoa.max_length} onChange={e=>setNewRoa({...newRoa, max_length: e.target.value})} type="number" placeholder="MaxLen" />
              </div>
              <div style={{flex:'none'}}>
                <button onClick={()=>addRoute(newRoa.prefix, newRoa.max_length)} disabled={!newRoa.prefix || !newRoa.max_length}>
                  <Plus size={12}/> Adicionar
                </button>
              </div>
            </div>
          </>
        )}
      </Section>

      {msg && <div style={{marginTop:'1rem', padding:'.6rem', background: msg.startsWith('✓')?'rgba(16,185,129,.1)':'rgba(245,158,11,.1)', border:'1px solid '+(msg.startsWith('✓')?'#10b981':'#f59e0b'), borderRadius:'6px', fontSize:'.85rem'}}>{msg}</div>}
    </>
  );
}

function StepCard({ n, ok, title, hint }) {
  return (
    <div style={{padding:'.8rem', background: ok ? 'rgba(16,185,129,.08)' : '#18181b', border:'1px solid ' + (ok ? '#10b981' : '#27272a'), borderRadius:'8px'}}>
      <div style={{display:'flex', alignItems:'center', gap:'.4rem', marginBottom:'.2rem'}}>
        <div style={{width:'22px', height:'22px', borderRadius:'50%', background: ok ? '#10b981' : '#27272a', display:'flex', alignItems:'center', justifyContent:'center', color: ok?'#fff':'#a1a1aa', fontSize:'.78rem', fontWeight:'600'}}>
          {ok ? '✓' : n}
        </div>
        <strong style={{fontSize:'.85rem'}}>{title}</strong>
      </div>
      <div style={{color:'#a1a1aa', fontSize:'.72rem', paddingLeft:'1.8rem'}}>{hint}</div>
    </div>
  );
}

function Section({ n, title, done, active = true, children }) {
  return (
    <div style={{marginTop:'1.2rem', padding:'1.2rem', border:'1px solid ' + (done ? '#10b981' : '#27272a'), borderRadius:'8px', opacity: active ? 1 : 0.6}}>
      <h3 style={{margin:0, marginBottom:'.6rem', display:'flex', alignItems:'center', gap:'.5rem'}}>
        <span style={{background: done?'#10b981':'#27272a', color: done?'#fff':'#a1a1aa', width:'24px', height:'24px', borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'.85rem'}}>{done ? '✓' : n}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function RpkiStatus({ domain }) {
  const [allAsns, setAllAsns] = useState([]);
  const [domainAsns, setDomainAsns] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [manual, setManual] = useState({ asn: '', prefix: '' });
  const [manualResult, setManualResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, d, c] = await Promise.all([
        api.get('/asn'),
        api.get(`/domains/${domain.name}/asns`),
        api.get(`/domains/${domain.name}/rpki/checklist`),
      ]);
      setAllAsns(a.data); setDomainAsns(d.data); setChecklist(c.data);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [domain.name]);

  const validateManual = async () => {
    if (!manual.asn || !manual.prefix) return;
    setManualResult('loading');
    try {
      const r = await api.get(`/rpki/validity?asn=${manual.asn}&prefix=${encodeURIComponent(manual.prefix)}`);
      setManualResult(r.data.validated_route);
    } catch (e) { setManualResult({ error: e.response?.data?.error?.error || 'Erro' }); }
  };

  const copyChecklist = () => {
    if (!checklist?.checks?.length) return;
    const lines = checklist.checks.map(c =>
      `${c.asn}\t${c.prefix}\tmaxLength=${c.max_length}\t# ${c.description || ''}`
    );
    const txt = `# ROAs para criar no Registro.br/LACNIC — ${domain.name}\n# ASN\tPrefixo\t\tMaxLength\tDescrição\n${lines.join('\n')}`;
    navigator.clipboard.writeText(txt);
    alert('Checklist copiado para a área de transferência');
  };

  return (
    <>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <h3 style={{margin:0}}>RPKI · ASNs vinculados a {domain.name}</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
            Cada domínio pode ter um ou mais ASNs com seus respectivos prefixos. Aqui você gera os
            dados prontos para criar ROAs no NIC.br/LACNIC.
          </p>
        </div>
        <div style={{display:'flex', gap:'.4rem'}}>
          <button onClick={()=>setShowAssign(true)} className="secondary">Atribuir ASNs</button>
          <button onClick={load} className="ghost" disabled={loading}><RefreshCw size={14}/> {loading?'…':'Atualizar'}</button>
        </div>
      </div>

      {/* ASNs do domínio */}
      <h4 style={{marginTop:'1rem'}}>ASNs deste domínio</h4>
      {domainAsns.length === 0 ? (
        <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', fontSize:'.88rem'}}>
          <strong style={{color:'#f59e0b'}}>⚠ Nenhum ASN atribuído</strong>
          <p style={{margin:'.3rem 0 0', color:'#a1a1aa'}}>
            Atribua um ou mais ASNs (registrados em <strong>Sistema → Recursos ASN</strong>) para validar RPKI deste domínio.
          </p>
        </div>
      ) : (
        <table>
          <thead><tr><th>ASN</th><th>Organização</th><th>Prefixos</th><th>Descrição</th></tr></thead>
          <tbody>
            {domainAsns.map(a => (
              <tr key={a.id}>
                <td><strong style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</strong></td>
                <td>{a.org_name || '–'}</td>
                <td>
                  {(a.prefixes || []).map(p => (
                    <span key={p.id} className="badge ok" style={{marginRight:'.3rem', fontFamily:'JetBrains Mono'}}>{p.prefix}</span>
                  ))}
                  {!a.prefixes?.length && <span style={{color:'#a1a1aa'}}>sem prefixos</span>}
                </td>
                <td style={{color:'#a1a1aa', fontSize:'.82rem'}}>{a.description || '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Checklist de ROAs */}
      {checklist?.checks?.length > 0 && (
        <>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'1.5rem'}}>
            <h4 style={{margin:0}}>Checklist de ROAs · {checklist.checks.length} entradas</h4>
            <button onClick={copyChecklist} className="secondary"><Copy size={12}/> Copiar lista</button>
          </div>
          <p style={{color:'#a1a1aa', fontSize:'.82rem', marginTop:'.3rem'}}>
            Cole estes dados em <a href="https://registro.br/tecnologia/recursos-de-numeracao/rpki/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>Registro.br → RPKI → Criar ROA</a>.
          </p>
          <table style={{marginTop:'.5rem'}}>
            <thead><tr><th>ASN</th><th>Prefixo</th><th>Max Length</th><th>Família</th><th>Status RPKI atual</th></tr></thead>
            <tbody>
              {checklist.checks.map((c, i) => {
                const meta = RPKI_STATE[c.state];
                const Icon = meta?.icon || AlertTriangle;
                return (
                  <tr key={i}>
                    <td style={{fontFamily:'JetBrains Mono'}}>{c.asn}</td>
                    <td style={{fontFamily:'JetBrains Mono'}}>{c.prefix}</td>
                    <td>{c.max_length}</td>
                    <td><span className="badge ok">{c.family}</span></td>
                    <td>
                      {meta ? (
                        <span style={{display:'inline-flex',alignItems:'center',gap:'.3rem',color:meta.color,fontWeight:600}}>
                          <Icon size={14}/> {meta.label}
                        </span>
                      ) : <span style={{color:'#a1a1aa'}}>{c.state}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Validador manual */}
      <h4 style={{marginTop:'2rem'}}>Validar ASN + prefixo manualmente</h4>
      <div className="row">
        <div>
          <label>ASN</label>
          <input value={manual.asn} onChange={e=>setManual({...manual, asn: e.target.value})}
            placeholder="AS65000" style={{fontFamily:'JetBrains Mono'}} />
        </div>
        <div>
          <label>Prefixo (CIDR)</label>
          <input value={manual.prefix} onChange={e=>setManual({...manual, prefix: e.target.value})}
            placeholder="203.0.113.0/24" style={{fontFamily:'JetBrains Mono'}} />
        </div>
        <div style={{flex:'none', paddingTop:'1.5rem'}}>
          <button onClick={validateManual}><Search size={12}/> Validar</button>
        </div>
      </div>
      {manualResult?.validity && (() => {
        const meta = RPKI_STATE[manualResult.validity.state] || { color:'#a1a1aa', label: manualResult.validity.state.toUpperCase(), icon: AlertTriangle };
        const Icon = meta.icon;
        return (
          <div style={{marginTop:'.8rem', padding:'.8rem 1rem', border:'1px solid ' + meta.color, borderRadius:'8px', background:'rgba(255,255,255,.02)'}}>
            <Icon size={16} color={meta.color}/> <strong style={{color: meta.color}}>{meta.label}</strong>
            <span style={{marginLeft:'.5rem', color:'#a1a1aa'}}>{manualResult.validity.description}</span>
          </div>
        );
      })()}

      {showAssign && (
        <AssignAsnsModal
          domain={domain}
          allAsns={allAsns}
          currentIds={domainAsns.map(a => a.id)}
          onClose={() => { setShowAssign(false); load(); }}
        />
      )}
    </>
  );
}

// ── Geofeed RFC 8805 — organizado por ASN (list-detail) ────────
function GeofeedTab({ domain }) {
  const [selectedAsn, setSelectedAsn] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (selectedAsn) {
    return <GeofeedAsnDetail
      domain={domain}
      asn={selectedAsn}
      onBack={() => { setSelectedAsn(null); setRefreshKey(k => k+1); }}
    />;
  }
  return <GeofeedAsnList domain={domain} key={refreshKey} onSelect={setSelectedAsn} />;
}

function GeofeedAsnList({ domain, onSelect }) {
  const [asns, setAsns] = useState(null);
  useEffect(() => {
    api.get(`/domains/${domain.name}/asns`).then(r => setAsns(r.data));
  }, [domain.name]);

  if (asns === null) return <p style={{color:'#a1a1aa'}}>Carregando…</p>;

  return (
    <>
      <div>
        <h3 style={{margin:0}}>Geofeed RFC 8805 — por ASN</h3>
        <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
          Cada ASN tem seu próprio Geofeed isolado, com URLs separadas por família IPv4/IPv6.
          Clique no ASN para configurar e gerar o CSV.
        </p>
      </div>

      {asns.length === 0 ? (
        <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', marginTop:'1rem'}}>
          <strong style={{color:'#f59e0b'}}>Nenhum ASN vinculado</strong>
          <p style={{color:'#a1a1aa', margin:'.3rem 0 0', fontSize:'.85rem'}}>
            Vá na aba <strong>RPKI</strong> e adicione um ASN para depois gerar o Geofeed dele.
          </p>
        </div>
      ) : (
        <table style={{marginTop:'1rem'}}>
          <thead><tr><th>Domínio</th><th>Organização</th><th>ASN</th><th>Prefixos</th><th></th></tr></thead>
          <tbody>
            {asns.map(a => (
              <tr key={a.id} onClick={() => onSelect(a)} style={{cursor:'pointer'}}>
                <td><strong>{domain.name}</strong></td>
                <td>{a.org_name || '–'}</td>
                <td style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</td>
                <td>
                  {(a.prefixes || []).map(p => (
                    <span key={p.id} className="badge ok" style={{marginRight:'.3rem', fontFamily:'JetBrains Mono'}}>{p.prefix}</span>
                  ))}
                  {!a.prefixes?.length && <span style={{color:'#a1a1aa'}}>sem prefixos</span>}
                </td>
                <td><button className="secondary"><Search size={12}/> Configurar Geofeed</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function GeofeedAsnDetail({ domain, asn, onBack }) {
  const [cnpj, setCnpj] = useState('35.048.089/0001-40');
  const [cnpjData, setCnpjData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [current, setCurrent] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/geofeed/${domain.name}/asn/${asn.id}`).then(r => {
    setCurrent(r.data);
    if (r.data?.entries?.length) setEntries(r.data.entries);
  }).catch(() => {});

  useEffect(() => {
    load();
    // Pré-popular se ainda não tem entries (primeira vez)
    if (entries.length === 0) {
      const initial = (asn.prefixes || []).map(p => ({
        prefix: p.prefix, country: 'BR', region: '', city: '', postal: '',
      }));
      if (initial.length) setEntries(initial);
    }
  }, [asn.id]);

  const fetchCnpj = async () => {
    setLoading(true); setMsg('');
    try {
      const cnpjClean = cnpj.replace(/\D/g, '');
      const r = await api.get(`/geofeed/cnpj/${cnpjClean}`);
      const d = r.data;
      setCnpjData(d);
      // Aplica nas entries
      const ufCode = d.uf ? `BR-${d.uf}` : '';
      const cep = d.cep ? d.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : '';
      setEntries(entries.map(e => ({ ...e, region: ufCode, city: d.municipio || '', postal: cep })));
      setMsg('✓ CNPJ carregado');
    } catch (e) { setMsg(e.response?.data?.error || 'Falha na consulta'); }
    setLoading(false);
  };

  const updateEntry = (i, field, value) => {
    const next = [...entries]; next[i] = { ...next[i], [field]: value }; setEntries(next);
  };
  const addEntry = () => setEntries([...entries, { prefix: '', country: 'BR', region: '', city: '', postal: '' }]);
  const removeEntry = (i) => setEntries(entries.filter((_, j) => j !== i));

  const save = async () => {
    setLoading(true); setMsg('Gerando arquivos…');
    try {
      const addressLine = cnpjData
        ? `${cnpjData.descricao_tipo_de_logradouro||''} ${cnpjData.logradouro||''}, ${cnpjData.numero||''} - ${cnpjData.bairro||''} - ${cnpjData.municipio||''}/${cnpjData.uf||''} - CEP ${cnpjData.cep?cnpjData.cep.replace(/(\d{5})(\d{3})/,'$1-$2'):''}`
        : '';
      const r = await api.post(`/geofeed/${domain.name}/asn/${asn.id}`, {
        entries,
        cnpj: cnpj || null,
        org_name: cnpjData?.razao_social || cnpjData?.nome_empresarial || asn.org_name || '',
        address_line: addressLine || null,
      });
      setMsg(`✓ Salvo. ${Object.keys(r.data.urls || {}).length} URL(s) gerada(s).`);
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
    setLoading(false);
  };

  return (
    <>
      <button onClick={onBack} className="secondary" style={{marginBottom:'1rem'}}>
        <ArrowLeft size={14}/> Voltar para lista de ASNs
      </button>

      {/* Header */}
      <div style={{padding:'1.2rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', marginBottom:'1.5rem'}}>
        <div style={{color:'#a1a1aa', fontSize:'.78rem', textTransform:'uppercase', letterSpacing:'.1em'}}>{domain.name} · Geofeed</div>
        <div style={{fontFamily:'JetBrains Mono', color:'#3b82f6', fontSize:'1.5rem', fontWeight:'700'}}>{asn.asn}</div>
        <div style={{color:'#a1a1aa'}}>{asn.org_name || 'Sem organização'}</div>
      </div>

      <h3>Geofeed RFC 8805 — {asn.asn}</h3>
      <p style={{color:'#a1a1aa', fontSize:'.88rem'}}>
        Arquivo CSV com geolocalização dos blocos IP deste ASN. Registre as URLs abaixo no painel do Registro.br
        (uma URL por bloco IP — IPv4 e IPv6 separados).
      </p>

      {current?.exists && (
        <div style={{padding:'1rem', background:'rgba(16,185,129,.05)', border:'1px solid #10b981', borderRadius:'8px', marginBottom:'1rem', fontSize:'.85rem'}}>
          <strong style={{color:'#10b981'}}>✓ Geofeed publicado para {asn.asn}</strong>
          <p style={{margin:'.5rem 0 .3rem', color:'#a1a1aa', fontSize:'.82rem'}}>
            <strong>Importante:</strong> Registro.br aceita apenas prefixos contidos no bloco. Use a URL correspondente em cada bloco IP:
          </p>
          <table style={{marginTop:'.4rem'}}>
            <thead><tr><th>Cadastrar no bloco</th><th>URL para colar no Registro.br</th></tr></thead>
            <tbody>
              {current.urls?.ipv4 && (asn.prefixes || []).filter(p => p.family === 'ipv4').map(p => (
                <tr key={'v4-' + p.id}>
                  <td><span className="badge ok">IPv4</span> <code>{p.prefix}</code></td>
                  <td>
                    <a href={current.urls.ipv4} target="_blank" rel="noreferrer" style={{color:'#3b82f6', fontFamily:'JetBrains Mono', fontSize:'.82rem'}}>{current.urls.ipv4}</a>
                    {' '}<button onClick={()=>navigator.clipboard.writeText(current.urls.ipv4)} className="ghost" style={{padding:'.2rem .4rem'}}><Copy size={10}/></button>
                  </td>
                </tr>
              ))}
              {current.urls?.ipv6 && (asn.prefixes || []).filter(p => p.family === 'ipv6').map(p => (
                <tr key={'v6-' + p.id}>
                  <td><span className="badge ok">IPv6</span> <code>{p.prefix}</code></td>
                  <td>
                    <a href={current.urls.ipv6} target="_blank" rel="noreferrer" style={{color:'#3b82f6', fontFamily:'JetBrains Mono', fontSize:'.82rem'}}>{current.urls.ipv6}</a>
                    {' '}<button onClick={()=>navigator.clipboard.writeText(current.urls.ipv6)} className="ghost" style={{padding:'.2rem .4rem'}}><Copy size={10}/></button>
                  </td>
                </tr>
              ))}
              {current.urls?.master && (
                <tr>
                  <td style={{color:'#a1a1aa'}}>Master (todos)</td>
                  <td><a href={current.urls.master} target="_blank" rel="noreferrer" style={{color:'#a1a1aa', fontFamily:'JetBrains Mono', fontSize:'.82rem'}}>{current.urls.master}</a></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* CNPJ — auto-popular endereço */}
      <div style={{padding:'1rem', border:'1px solid #27272a', borderRadius:'8px', marginTop:'1rem'}}>
        <h4 style={{margin:0}}>Endereço da empresa (via CNPJ)</h4>
        <div className="row" style={{marginTop:'.5rem'}}>
          <input value={cnpj} onChange={e=>setCnpj(e.target.value)} placeholder="00.000.000/0000-00" />
          <div style={{flex:'none'}}>
            <button onClick={fetchCnpj} disabled={loading}><Search size={12}/> Buscar</button>
          </div>
        </div>
        {cnpjData && (
          <div style={{marginTop:'.6rem', padding:'.6rem', background:'#0a0a0a', borderRadius:'6px', fontSize:'.82rem'}}>
            <div><strong>{cnpjData.razao_social || cnpjData.nome_empresarial}</strong></div>
            <div style={{color:'#a1a1aa'}}>
              {cnpjData.descricao_tipo_de_logradouro} {cnpjData.logradouro}, {cnpjData.numero}
              {cnpjData.complemento ? ` (${cnpjData.complemento})` : ''}
              <br/>
              {cnpjData.bairro} — {cnpjData.municipio}/{cnpjData.uf} — CEP {cnpjData.cep?.replace(/(\d{5})(\d{3})/,'$1-$2')}
            </div>
          </div>
        )}
      </div>

      {/* Entries */}
      <h4 style={{marginTop:'1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <span>Entradas Geofeed ({entries.length})</span>
        <button onClick={addEntry} className="secondary"><Plus size={12}/> Adicionar bloco</button>
      </h4>
      <table>
        <thead>
          <tr>
            <th>Prefixo IP</th><th>País</th><th>Região</th><th>Cidade</th><th>CEP</th><th></th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i}>
              <td><input value={e.prefix} onChange={ev=>updateEntry(i,'prefix',ev.target.value)} placeholder="203.0.113.0/24" style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}} /></td>
              <td style={{maxWidth:'80px'}}><input value={e.country} onChange={ev=>updateEntry(i,'country',ev.target.value)} placeholder="BR" /></td>
              <td style={{maxWidth:'90px'}}><input value={e.region} onChange={ev=>updateEntry(i,'region',ev.target.value)} placeholder="BR-SP" /></td>
              <td><input value={e.city} onChange={ev=>updateEntry(i,'city',ev.target.value)} placeholder="Cidade" /></td>
              <td style={{maxWidth:'120px'}}><input value={e.postal} onChange={ev=>updateEntry(i,'postal',ev.target.value)} placeholder="00000-000" /></td>
              <td><button className="danger" onClick={()=>removeEntry(i)}><Trash2 size={12}/></button></td>
            </tr>
          ))}
          {entries.length === 0 && <tr><td colSpan={6} style={{color:'#a1a1aa'}}>Sem entradas. Clique "+ Adicionar bloco".</td></tr>}
        </tbody>
      </table>

      <div style={{marginTop:'1rem'}}>
        <button onClick={save} disabled={loading || entries.length === 0}>
          <Save size={12}/> {loading ? 'Salvando…' : 'Gerar/atualizar geofeed.csv'}
        </button>
      </div>

      {msg && <div style={{marginTop:'.8rem', color: msg.startsWith('✓')?'#10b981':'#a1a1aa'}}>{msg}</div>}

      {current?.content && (
        <>
          <h4 style={{marginTop:'1.5rem'}}>Conteúdo atual</h4>
          <pre className="code" style={{maxHeight:'250px', overflow:'auto', fontSize:'.78rem'}}>{current.content}</pre>
        </>
      )}

      <div style={{marginTop:'2rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
        <strong style={{color:'#3b82f6'}}>Como registrar no Registro.br:</strong>
        <ol style={{margin:'.5rem 0 0 1.2rem', padding:0, color:'#a1a1aa'}}>
          <li>Acesse <a href="https://registro.br/tecnologia/numeracao/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>registro.br/tecnologia/numeracao</a> → seu bloco IP</li>
          <li>Procure por "Configuração GEOFEED"</li>
          <li>Cole a URL: <code>https://{domain.name}/geofeed.csv</code></li>
          <li>O Registro.br valida o arquivo (RFC 8805, máx 5MB)</li>
        </ol>
      </div>
    </>
  );
}

// ── RPKI CA inline (Krill) ────────────────────────────────────
function RpkiCAInline({ domain }) {
  const [cas, setCas] = useState([]);
  const [selectedHandle, setSelectedHandle] = useState('');
  const [childRequest, setChildRequest] = useState('');
  const [pubRequest, setPubRequest] = useState('');
  const [parentXml, setParentXml] = useState('');
  const [parentHandle, setParentHandle] = useState('registro-br');
  const [creating, setCreating] = useState(false);
  const [newHandle, setNewHandle] = useState('hiperlink');
  const [msg, setMsg] = useState('');

  const loadCas = async () => {
    try {
      const r = await api.get('/krill/cas');
      const list = r.data?.cas || [];
      setCas(list);
      if (list[0] && !selectedHandle) setSelectedHandle(typeof list[0] === 'string' ? list[0] : list[0].handle);
    } catch (e) { setMsg(e.response?.data?.error || 'Krill inacessível'); }
  };
  useEffect(() => { loadCas(); }, []);

  const loadXmls = async () => {
    if (!selectedHandle) return;
    try {
      const [cr, pr] = await Promise.allSettled([
        api.get(`/krill/cas/${selectedHandle}/child_request`, { responseType: 'text', transformResponse: r => r }),
        api.get(`/krill/cas/${selectedHandle}/publisher_request`, { responseType: 'text', transformResponse: r => r }),
      ]);
      if (cr.status === 'fulfilled') setChildRequest(cr.value.data);
      if (pr.status === 'fulfilled') setPubRequest(pr.value.data);
    } catch (e) {}
  };
  useEffect(() => { loadXmls(); }, [selectedHandle]);

  const createCA = async () => {
    setCreating(true); setMsg('Criando CA…');
    try {
      await api.post('/krill/cas', { handle: newHandle });
      setMsg('✓ CA criada');
      setSelectedHandle(newHandle);
      await loadCas();
    } catch (e) { setMsg(e.response?.data?.error?.toString() || 'Erro'); }
    setCreating(false);
  };

  const download = (filename, content) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  };

  const submitParent = async () => {
    setMsg('Enviando parent response…');
    try {
      await api.post(`/krill/cas/${selectedHandle}/parent`, { parentHandle, response: parentXml });
      setMsg('✓ Parent registrado'); setParentXml('');
    } catch (e) { setMsg(e.response?.data?.error?.toString() || 'Erro'); }
  };

  return (
    <>
      <h3 style={{margin:0}}>Certification Authority (Krill)</h3>
      <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
        Gere o <strong>child_request.xml</strong> aqui, submeta no Registro.br no modo "RPKI Delegado",
        e depois faça upload do <strong>parent_response.xml</strong> recebido.
      </p>

      {cas.length === 0 ? (
        <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', fontSize:'.88rem'}}>
          <strong style={{color:'#f59e0b'}}>Nenhuma CA criada ainda</strong>
          <div className="row" style={{marginTop:'.6rem'}}>
            <div style={{flex:1}}>
              <input value={newHandle} onChange={e=>setNewHandle(e.target.value)}
                placeholder="hiperlink" style={{fontFamily:'JetBrains Mono'}} />
              <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
                Handle único (sem espaços/acentos). Vai aparecer no child_request.xml.
              </p>
            </div>
            <div style={{flex:'none', paddingTop:'.2rem'}}>
              <button onClick={createCA} disabled={creating || !newHandle}>
                {creating ? 'Criando…' : 'Criar CA'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <label>CA selecionada</label>
          <select value={selectedHandle} onChange={e=>setSelectedHandle(e.target.value)} style={{maxWidth:'300px', fontFamily:'JetBrains Mono'}}>
            {cas.map((c, i) => {
              const h = typeof c === 'string' ? c : c.handle;
              return <option key={i} value={h}>{h}</option>;
            })}
          </select>

          {selectedHandle && (
            <>
              {/* Child Request */}
              <div style={{marginTop:'1.5rem', padding:'1rem', border:'1px solid #27272a', borderRadius:'8px'}}>
                <h4 style={{margin:0, color:'#3b82f6'}}>📤 1. Child Request (para submeter no Registro.br)</h4>
                <p style={{color:'#a1a1aa', fontSize:'.85rem', margin:'.4rem 0'}}>
                  Baixe e submeta em <a href="https://registro.br/tecnologia/numeracao/rpki/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>registro.br → RPKI Delegado <ExternalLink size={10}/></a>
                </p>
                <div style={{display:'flex', gap:'.4rem', marginBottom:'.5rem'}}>
                  <button onClick={()=>download(`child_request_${selectedHandle}.xml`, childRequest)} disabled={!childRequest}>
                    <Download size={12}/> Baixar XML
                  </button>
                  <button onClick={()=>{navigator.clipboard.writeText(childRequest); setMsg('Copiado'); }} disabled={!childRequest} className="secondary">
                    <Copy size={12}/> Copiar
                  </button>
                </div>
                <pre className="code" style={{maxHeight:'200px', overflow:'auto', fontSize:'.7rem'}}>{childRequest || '(carregando…)'}</pre>
              </div>

              {/* Publisher Request */}
              <div style={{marginTop:'1rem', padding:'1rem', border:'1px solid #27272a', borderRadius:'8px'}}>
                <h4 style={{margin:0, color:'#3b82f6'}}>📤 1b. Publisher Request (opcional — para publicar via Registro.br)</h4>
                <p style={{color:'#a1a1aa', fontSize:'.85rem', margin:'.4rem 0'}}>
                  Use se quiser que o Registro.br publique suas ROAs ao invés do seu próprio servidor.
                </p>
                <button onClick={()=>download(`publisher_request_${selectedHandle}.xml`, pubRequest)} disabled={!pubRequest}>
                  <Download size={12}/> Baixar XML
                </button>
              </div>

              {/* Parent Response */}
              <div style={{marginTop:'1rem', padding:'1rem', border:'1px solid #27272a', borderRadius:'8px'}}>
                <h4 style={{margin:0, color:'#10b981'}}>📥 2. Parent Response (cole o XML recebido do Registro.br)</h4>
                <label>Parent Handle</label>
                <input value={parentHandle} onChange={e=>setParentHandle(e.target.value)}
                  placeholder="registro-br" style={{maxWidth:'300px'}} />
                <label>Parent Response XML</label>
                <textarea value={parentXml} onChange={e=>setParentXml(e.target.value)}
                  placeholder="<parent_response ...>...</parent_response>"
                  style={{minHeight:'180px', fontFamily:'JetBrains Mono', fontSize:'.78rem'}} />
                <div style={{marginTop:'.6rem'}}>
                  <button onClick={submitParent} disabled={!parentXml}>
                    <Upload size={12}/> Enviar parent response
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {msg && <div style={{marginTop:'.6rem', color: msg.startsWith('✓') ? '#10b981' : '#a1a1aa'}}>{msg}</div>}
    </>
  );
}

// ── ROAs inline ────────────────────────────────────────────────
function RpkiRoasInline({ domain }) {
  const [domainAsns, setDomainAsns] = useState([]);
  const [cas, setCas] = useState([]);
  const [selectedHandle, setSelectedHandle] = useState('');
  const [roas, setRoas] = useState([]);
  const [newRoa, setNewRoa] = useState({ asn: '', prefix: '', max_length: '' });
  const [msg, setMsg] = useState('');

  const loadAll = async () => {
    try {
      const [c, da] = await Promise.all([
        api.get('/krill/cas'),
        api.get(`/domains/${domain.name}/asns`),
      ]);
      const list = c.data?.cas || [];
      setCas(list);
      setDomainAsns(da.data);
      if (list[0] && !selectedHandle) setSelectedHandle(typeof list[0] === 'string' ? list[0] : list[0].handle);
    } catch {}
  };
  useEffect(() => { loadAll(); }, []);

  const loadRoas = async () => {
    if (!selectedHandle) return;
    try {
      const r = await api.get(`/krill/cas/${selectedHandle}/routes`);
      setRoas(r.data || []);
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };
  useEffect(() => { loadRoas(); }, [selectedHandle]);

  const addRoa = async () => {
    if (!newRoa.asn || !newRoa.prefix) return;
    setMsg('Criando ROA…');
    try {
      const cidr = newRoa.prefix.split('/');
      await api.post(`/krill/cas/${selectedHandle}/routes`, {
        added: [{
          asn: parseInt(newRoa.asn.replace(/^AS/i, ''), 10),
          prefix: newRoa.prefix,
          max_length: parseInt(newRoa.max_length || cidr[1], 10),
        }],
      });
      setMsg('✓ ROA criada — propaga em ~30 min');
      setNewRoa({ asn: '', prefix: '', max_length: '' });
      loadRoas();
    } catch (e) { setMsg(e.response?.data?.error?.toString() || 'Erro'); }
  };

  const removeRoa = async (r) => {
    if (!window.confirm(`Remover ROA AS${r.asn} ${r.prefix} maxLen=${r.max_length}?`)) return;
    try {
      await api.post(`/krill/cas/${selectedHandle}/routes`, {
        removed: [{ asn: r.asn, prefix: r.prefix, max_length: r.max_length }],
      });
      loadRoas();
    } catch (e) { alert(e.response?.data?.error?.toString() || 'Erro'); }
  };

  // Sugerir adicionar ROAs faltantes dos ASNs do domínio
  const suggestions = [];
  domainAsns.forEach(a => {
    (a.prefixes || []).forEach(p => {
      const asNum = parseInt(a.asn.replace(/^AS/i, ''), 10);
      const exists = roas.some(r => r.asn === asNum && r.prefix === p.prefix);
      if (!exists) suggestions.push({ asn: a.asn, prefix: p.prefix, max_length: p.max_length });
    });
  });

  return (
    <>
      <h3 style={{margin:0}}>ROAs Publicadas pelo Krill</h3>
      <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>
        Cria/remove ROAs locais. Após criada, propaga em ~30 min para Routinator e outros validators globais.
      </p>

      {cas.length === 0 ? (
        <p style={{color:'#a1a1aa', marginTop:'1rem'}}>
          Sem CA configurada. Vá na aba "CA Delegated" primeiro.
        </p>
      ) : (
        <>
          <label>CA</label>
          <select value={selectedHandle} onChange={e=>setSelectedHandle(e.target.value)} style={{maxWidth:'300px'}}>
            {cas.map((c, i) => {
              const h = typeof c === 'string' ? c : c.handle;
              return <option key={i} value={h}>{h}</option>;
            })}
          </select>

          {/* Sugestões */}
          {suggestions.length > 0 && (
            <div style={{marginTop:'1rem', padding:'.8rem', background:'rgba(59,130,246,.08)', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
              <strong style={{color:'#3b82f6'}}>💡 ROAs sugeridas para os ASNs deste domínio:</strong>
              <div style={{marginTop:'.5rem'}}>
                {suggestions.map((s, i) => (
                  <div key={i} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.3rem 0'}}>
                    <code>{s.asn} → {s.prefix} (maxLen={s.max_length})</code>
                    <button className="secondary" onClick={()=>setNewRoa({asn:s.asn, prefix:s.prefix, max_length:String(s.max_length)})}>
                      <Plus size={12}/> Usar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h4 style={{marginTop:'1.5rem'}}>ROAs ativas ({roas.length})</h4>
          <table>
            <thead><tr><th>ASN</th><th>Prefixo</th><th>Max Length</th><th></th></tr></thead>
            <tbody>
              {roas.length === 0
                ? <tr><td colSpan={4} style={{color:'#a1a1aa'}}>Nenhuma ROA. Crie usando sugestões acima ou o formulário abaixo.</td></tr>
                : roas.map((r, i) => (
                  <tr key={i}>
                    <td style={{fontFamily:'JetBrains Mono'}}>AS{r.asn}</td>
                    <td style={{fontFamily:'JetBrains Mono'}}>{r.prefix}</td>
                    <td>{r.max_length}</td>
                    <td><button className="danger" onClick={()=>removeRoa(r)}><Trash2 size={12}/></button></td>
                  </tr>
                ))}
            </tbody>
          </table>

          <h4 style={{marginTop:'1.5rem'}}>Adicionar ROA</h4>
          <div className="row">
            <div>
              <label>ASN</label>
              <input value={newRoa.asn} onChange={e=>setNewRoa({...newRoa, asn: e.target.value})} placeholder="AS65000" style={{fontFamily:'JetBrains Mono'}} />
            </div>
            <div>
              <label>Prefixo CIDR</label>
              <input value={newRoa.prefix} onChange={e=>setNewRoa({...newRoa, prefix: e.target.value})} placeholder="203.0.113.0/24" style={{fontFamily:'JetBrains Mono'}} />
            </div>
            <div style={{maxWidth:'140px'}}>
              <label>Max length</label>
              <input value={newRoa.max_length} onChange={e=>setNewRoa({...newRoa, max_length: e.target.value})} type="number" placeholder="24" />
            </div>
            <div style={{flex:'none', paddingTop:'1.5rem'}}>
              <button onClick={addRoa} disabled={!newRoa.asn || !newRoa.prefix}><Plus size={12}/> Criar</button>
            </div>
          </div>

          {msg && <div style={{marginTop:'.6rem', color: msg.startsWith('✓') ? '#10b981' : '#a1a1aa'}}>{msg}</div>}
        </>
      )}
    </>
  );
}

function AssignAsnsModal({ domain, allAsns, currentIds, onClose }) {
  const [selected, setSelected] = useState(currentIds);
  const [msg, setMsg] = useState('');

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const save = async () => {
    setMsg('Salvando…');
    try {
      await api.post(`/domains/${domain.name}/asns`, { asnIds: selected });
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'560px'}}>
        <h2>Atribuir ASNs a {domain.name}</h2>
        <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
          Selecione todos os ASNs sob os quais este domínio é anunciado. Não vê o seu ASN aqui?
          Cadastre em <strong>Sistema → Recursos ASN</strong>.
        </p>
        <div style={{maxHeight:'350px', overflow:'auto', border:'1px solid #27272a', borderRadius:'6px', padding:'.5rem', marginTop:'.5rem'}}>
          {allAsns.length === 0
            ? <p style={{color:'#a1a1aa', margin:0, padding:'.5rem'}}>Nenhum ASN cadastrado. Vá em Sistema → Recursos ASN.</p>
            : allAsns.map(a => (
              <label key={a.id} style={{display:'flex', alignItems:'center', gap:'.5rem', padding:'.5rem 0', cursor:'pointer', borderBottom:'1px solid #1f1f23'}}>
                <input type="checkbox" style={{width:'auto'}} checked={selected.includes(a.id)} onChange={()=>toggle(a.id)} />
                <div style={{flex:1}}>
                  <strong style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{a.asn}</strong>
                  {a.org_name && <span style={{marginLeft:'.5rem', color:'#a1a1aa'}}>{a.org_name}</span>}
                </div>
              </label>
            ))}
        </div>
        {msg && <div className="err-msg">{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save}><Save size={12}/> Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ── EMAIL ──────────────────────────────────────────────────────
function EmailBackupCard({ domain }) {
  const [backups, setBackups] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => api.get(`/email/${domain.name}/backup/list`).then(r => setBackups(r.data.files || []));
  useEffect(() => { load(); }, [domain.name]);

  const generate = async () => {
    if (!window.confirm(`Gerar backup completo das caixas postais de ${domain.name}?\n\nVai incluir todas as mensagens, contas, senhas (hash), DKIM e configs Postfix/Dovecot — tudo num .tar.gz pronto pra restaurar em outro servidor.`)) return;
    setGenerating(true); setMsg(null);
    try {
      const r = await api.post(`/email/${domain.name}/backup`, {});
      setMsg({ kind: 'ok', text: `Backup gerado: ${r.data.filename} (${r.data.size_human})` });
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || 'Erro ao gerar backup' });
    } finally { setGenerating(false); }
  };

  const downloadFile = async (b) => {
    try {
      const resp = await api.get(b.download_url, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = b.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg({ kind: 'err', text: 'Erro ao baixar: ' + (e.response?.data?.error || e.message) }); }
  };

  const del = async (b) => {
    if (!window.confirm(`Apagar ${b.filename}?`)) return;
    try {
      await api.delete(`/email/${domain.name}/backup/${b.filename}`);
      load();
    } catch (e) { setMsg({ kind: 'err', text: e.response?.data?.error || 'Erro' }); }
  };

  return (
    <div className="card">
      <div className="card-header">
        <HardDrive size={15} color="var(--accent)"/>
        <h3 className="h3">Backup para migração de servidor</h3>
        <div className="card-actions">
          <button className="btn btn-primary btn-sm" onClick={generate} disabled={generating}>
            <Download size={13}/> {generating ? 'Gerando…' : 'Gerar backup agora'}
          </button>
        </div>
      </div>
      <div className="card-body stack-sm">
        <p className="dim" style={{fontSize:13, lineHeight:1.6, margin:0}}>
          O tarball inclui: <strong>todas as mensagens</strong> (Maildirs), credenciais com senhas em hash,
          chaves DKIM, e snippets de config Postfix + Dovecot prontos pra colar num servidor novo. Use pra migrar sem perder nada.
        </p>
        {msg && (
          <div className={msg.kind === 'ok' ? 'ok-msg' : 'err-msg'} style={{margin:0}}>{msg.text}</div>
        )}
        {backups.length > 0 && (
          <table className="table" style={{marginTop:8}}>
            <thead><tr><th>Arquivo</th><th>Tamanho</th><th>Criado em</th><th style={{width:160}}></th></tr></thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.filename}>
                  <td className="mono" style={{fontSize:12}}>{b.filename}</td>
                  <td className="mono">{b.size_human}</td>
                  <td className="dim" style={{fontSize:12}}>{new Date(b.created_at).toLocaleString('pt-BR')}</td>
                  <td style={{textAlign:'right'}}>
                    <button className="btn btn-secondary btn-sm" onClick={()=>downloadFile(b)}>
                      <Download size={12}/> Baixar
                    </button>{' '}
                    <button className="btn btn-ghost btn-icon" onClick={()=>del(b)} title="Apagar">
                      <Trash2 size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Email({ domain }) {
  const [accs, setAccs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', quotaMb: 1024, forwardTo: '' });
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/email/accounts').then(r => setAccs(r.data.filter(a => a.domain_name === domain.name)));
  useEffect(() => { load(); }, [domain.name]);

  const create = async () => {
    try {
      await api.post('/email/accounts', { domainId: domain.id, ...form });
      setShow(false); setForm({ username: '', password: '', quotaMb: 1024, forwardTo: '' });
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  const del = async (id) => {
    if (!window.confirm('Remover esta conta? (mensagens armazenadas permanecem no disco)')) return;
    await api.delete(`/email/accounts/${id}`); load();
  };

  if (!domain.has_email) return <p style={{color:'#a1a1aa'}}>E-mail não está ativo para este domínio.</p>;

  return (
    <div className="stack">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h3 style={{margin:0}}>Contas de E-mail</h3>
        <button onClick={()=>setShow(true)}><Plus size={14}/> Nova Conta</button>
      </div>

      <table style={{marginTop:'1rem'}}>
        <thead><tr><th>E-mail</th><th>Quota</th><th>Redirecionar para</th><th>Criado</th><th>Ações</th></tr></thead>
        <tbody>
          {accs.map(a => (
            <tr key={a.id}>
              <td><strong>{a.username}@{a.domain_name}</strong></td>
              <td>{a.quota_mb} MB</td>
              <td style={{fontFamily:'JetBrains Mono', fontSize:'.82rem'}}>{a.forward_to || '–'}</td>
              <td style={{color:'#a1a1aa'}}>{new Date(a.created_at).toLocaleString('pt-BR')}</td>
              <td>
                <button className="secondary" onClick={()=>setEditing(a)}><Edit3 size={12}/></button>{' '}
                <button className="danger" onClick={()=>del(a.id)}><Trash2 size={12}/></button>
              </td>
            </tr>
          ))}
          {accs.length === 0 && <tr><td colSpan={5} style={{color:'#a1a1aa'}}>Nenhuma conta.</td></tr>}
        </tbody>
      </table>

      {show && (
        <div className="modal-bg" onClick={()=>setShow(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h2>Nova Conta de E-mail</h2>
            <div className="row">
              <div>
                <label>Usuário</label>
                <div style={{display:'flex', alignItems:'center', gap:'.5rem'}}>
                  <input value={form.username} onChange={e=>setForm({...form, username: e.target.value})} placeholder="usuario" />
                  <span style={{color:'#a1a1aa', whiteSpace:'nowrap'}}>@{domain.name}</span>
                </div>
              </div>
              <div style={{maxWidth:'150px'}}>
                <label>Quota (MB)</label>
                <input type="number" value={form.quotaMb} onChange={e=>setForm({...form, quotaMb: +e.target.value})} />
              </div>
            </div>
            <label>Senha</label>
            <input type="password" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} />
            <label>Redirecionar para (opcional)</label>
            <input value={form.forwardTo} onChange={e=>setForm({...form, forwardTo: e.target.value})}
              placeholder="outro@email.com (deixe vazio para entrega normal)" />
            <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
              Quando preenchido, mensagens recebidas serão encaminhadas para esse endereço.
            </p>
            <div className="modal-actions">
              <button className="secondary" onClick={()=>setShow(false)}>Cancelar</button>
              <button onClick={create} disabled={!form.username || !form.password}>Criar</button>
            </div>
          </div>
        </div>
      )}

      {editing && <EditAccountModal acc={editing} onClose={()=>{ setEditing(null); load(); }} />}

      <EmailBackupCard domain={domain} />
    </div>
  );
}

function EditAccountModal({ acc, onClose }) {
  const [password, setPassword]   = useState('');
  const [quotaMb, setQuotaMb]     = useState(acc.quota_mb);
  const [forwardTo, setForwardTo] = useState(acc.forward_to || '');
  const [msg, setMsg] = useState('');

  const save = async () => {
    setMsg('Salvando…');
    try {
      const body = { quotaMb, forwardTo };
      if (password) body.password = password;
      await api.put(`/email/accounts/${acc.id}`, body);
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>Editar — {acc.username}@{acc.domain_name}</h2>
        <label>Nova senha (deixe vazio para manter a atual)</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} />
        <label>Quota (MB)</label>
        <input type="number" value={quotaMb} onChange={e=>setQuotaMb(+e.target.value)} />
        <label>Redirecionar para</label>
        <input value={forwardTo} onChange={e=>setForwardTo(e.target.value)}
          placeholder="outro@email.com (vazio = sem redirecionamento)" />
        {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro') ? '#ef4444':'#10b981'}}>{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save}><Save size={12}/> Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ── WEBMAIL ─────────────────────────────────────────────────────
function SpeedtestTab({ domain }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ server_id: '', license_key: '', sponsor: '' });

  const load = () => api.get(`/speedtest/${domain.name}/status`).then(r => setStatus(r.data)).catch(()=>{});
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [domain.name]);

  const provision = async () => {
    if (!window.confirm(`Provisionar speed.${domain.name}? Cria DNS + emite cert SSL para ficar pronto pra registro na Ookla.`)) return;
    setBusy(true); setOut(''); setErr('');
    try {
      const r = await api.post(`/speedtest/${domain.name}/provision`, {});
      setOut(r.data.output || '');
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro');
      setOut(e.response?.data?.output || e.message);
    } finally { setBusy(false); }
  };

  const configure = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/speedtest/${domain.name}/configure`, form);
      load();
      setForm({ server_id: '', license_key: '', sponsor: '' });
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro');
    } finally { setBusy(false); }
  };

  if (!status) return <div className="dim">Carregando status…</div>;

  const StepDot = ({ ok, n }) => (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: ok ? 'var(--ok-dim)' : 'var(--bg-3)',
      color: ok ? 'var(--ok)' : 'var(--text-mute)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 12,
      border: ok ? '1px solid rgba(63,185,80,0.3)' : '1px solid var(--border)',
      flexShrink: 0,
    }}>{ok ? <CheckCircle2 size={16}/> : n}</div>
  );

  return (
    <div className="stack">
      <div>
        <h2 className="h2">Speedtest Server (Ookla)</h2>
        <p className="dim" style={{fontSize:13, marginTop:4}}>
          Servidor oficial Ookla em <code>speed.{domain.name}</code>.
          O daemon escuta TCP/UDP <code>8080</code> direto no IP público; nginx só serve a landing.
        </p>
      </div>

      {/* Stepper de progresso */}
      <div className="card">
        <div className="card-header">
          <Activity size={15} color="var(--accent)"/>
          <h3 className="h3">Progresso do servidor</h3>
          <span className={'badge ' + (status.enabled && status.daemon.tcp_8080 ? 'ok' : status.dns.ok ? 'warn' : 'muted')} style={{marginLeft:'auto'}}>
            {status.enabled && status.daemon.tcp_8080 ? 'Online' : status.dns.ok ? 'Em configuração' : 'Não provisionado'}
          </span>
        </div>
        <div className="card-body stack">
          {[
            { n:1, ok: status.dns.ok,             title:'DNS configurado',       detail: status.dns.ok ? `A: ${status.dns.a.join(', ')} · AAAA: ${status.dns.aaaa.join(', ') || '–'}` : 'Crie speed.'+domain.name },
            { n:2, ok: status.cert.present,       title:'Certificado SSL emitido', detail: status.cert.present ? `Válido até ${status.cert.expires}` : 'Sem certificado Let\'s Encrypt' },
            { n:3, ok: !!status.server_id,        title:'Credenciais Ookla cadastradas', detail: status.server_id ? `Server ID: ${status.server_id} · License: ${status.license_key}` : 'Aguardando aprovação em speedtest.net/host' },
            { n:4, ok: status.daemon.tcp_8080,    title:'Daemon OoklaServer escutando porta 8080', detail: status.daemon.tcp_8080 ? 'Porta 8080 respondendo' : 'Daemon parado · porta 8080 fechada' },
          ].map(s => (
            <div key={s.n} className="hrow" style={{gap:12, padding:'8px 0'}}>
              <StepDot ok={s.ok} n={s.n}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:500, fontSize:13.5}}>{s.title}</div>
                <div className="dim mono" style={{fontSize:11.5}}>{s.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Passo 1+2: Provisionar DNS+cert */}
      {!status.dns.ok || !status.cert.present ? (
        <div className="card">
          <div className="card-header">
            <Plus size={15} color="var(--accent)"/>
            <h3 className="h3">1. Provisionar speed.{domain.name}</h3>
          </div>
          <div className="card-body stack">
            <p className="dim" style={{fontSize:13}}>
              Cria o registro DNS A/AAAA e emite certificado Let's Encrypt automaticamente.
              Após isso, o subdomínio fica pronto para você submeter à Ookla.
            </p>
            <div>
              <button className="btn btn-primary" onClick={provision} disabled={busy}>
                <Plus size={13}/> {busy ? 'Provisionando…' : 'Provisionar DNS + SSL'}
              </button>
            </div>
          </div>
        </div>
      ) : !status.server_id ? (
        // Passo 3: Mostrar info para submissão Ookla
        <div className="card">
          <div className="card-header">
            <ExternalLink size={15} color="var(--accent)"/>
            <h3 className="h3">2. Submeter à Ookla</h3>
          </div>
          <div className="card-body stack">
            <p className="dim" style={{fontSize:13.5, lineHeight:1.6}}>
              Acesse <a href="https://www.speedtest.net/host" target="_blank" rel="noreferrer">speedtest.net/host</a> e
              preencha o formulário com os dados abaixo. A Ookla envia o binário
              <code> OoklaServer</code> e as credenciais (server ID + license key) por e-mail após aprovação (geralmente 1-2 semanas).
            </p>

            <div className="grid-2" style={{fontSize:13}}>
              {[
                ['Hostname/IP do servidor', status.hostname],
                ['IPv4 público',           status.ipv4],
                ['IPv6 público',           status.ipv6 || '—'],
                ['ASN',                    'AS65000'],
                ['Organização',            'Hiperlink Telecom LTDA'],
                ['Localização',            'Brasil (preencher cidade real)'],
                ['Bandwidth disponível',   'Informar (mínimo 1 Gbps recomendado)'],
                ['E-mail técnico',         `admin@${domain.name}`],
              ].map(([k, v]) => (
                <div key={k} style={{padding:'10px 12px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8}}>
                  <div className="lbl">{k}</div>
                  <div className="mono" style={{fontSize:12.5, marginTop:4}}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{padding:'12px 14px', background:'var(--info-dim)', border:'1px solid rgba(88,166,255,.25)', borderRadius:8, fontSize:13, color:'var(--text-dim)'}}>
              <strong style={{color:'var(--info)'}}>💡 Após aprovação:</strong> volte aqui e preencha o
              formulário abaixo com Server ID + License Key recebidos.
            </div>

            <h4 className="h3" style={{marginTop:8}}>Cadastrar credenciais Ookla</h4>
            <div className="field">
              <label>Server ID</label>
              <input className="input mono" value={form.server_id} onChange={e=>setForm({...form, server_id:e.target.value})} placeholder="12345"/>
            </div>
            <div className="field">
              <label>License Key</label>
              <input className="input mono" value={form.license_key} onChange={e=>setForm({...form, license_key:e.target.value})} placeholder="abcdef0123456789..."/>
            </div>
            <div className="field">
              <label>Sponsor (nome do ISP exibido no app)</label>
              <input className="input" value={form.sponsor} onChange={e=>setForm({...form, sponsor:e.target.value})} placeholder="Hiperlink Internet"/>
            </div>
            <div>
              <button className="btn btn-primary" onClick={configure} disabled={busy || !form.server_id || !form.license_key}>
                <CheckCircle2 size={13}/> Salvar credenciais
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Passo 4: Daemon
        <div className="card">
          <div className="card-header">
            <Server size={15} color="var(--accent)"/>
            <h3 className="h3">3. Daemon OoklaServer</h3>
          </div>
          <div className="card-body stack">
            <p className="dim" style={{fontSize:13.5, lineHeight:1.6}}>
              Credenciais salvas. O arquivo <code>OoklaServer.properties</code> foi gerado em
              <code> /opt/TGuthConsulting/ookla/server/</code>. Próximos passos manuais:
            </p>
            <ol style={{margin:0, paddingLeft:20, fontSize:13, color:'var(--text-dim)', lineHeight:1.8}}>
              <li>Extrair o tarball <code>OoklaServer-linux64.tgz</code> recebido pela Ookla em <code>/opt/TGuthConsulting/ookla/server/</code></li>
              <li>Descomentar o serviço <code>ookla_speedtest</code> em <code>docker-compose.yml</code></li>
              <li>Rodar: <code>docker compose up -d ookla_speedtest</code></li>
              <li>Abrir portas TCP/UDP 8080 e TCP 5060 no firewall</li>
            </ol>
            <div className={'badge ' + (status.daemon.tcp_8080 ? 'ok' : 'warn')} style={{alignSelf:'flex-start'}}>
              {status.daemon.tcp_8080 ? '✓ Daemon respondendo' : '✗ Daemon não detectado em 8080'}
            </div>
          </div>
        </div>
      )}

      {/* Landing page */}
      {status.dns.ok && status.cert.present && (
        <div className="card">
          <div className="card-header">
            <Globe size={15} color="var(--accent)"/>
            <h3 className="h3">Landing pública</h3>
            <a href={`https://${status.hostname}`} target="_blank" rel="noreferrer" className="card-actions">
              <button className="btn btn-secondary btn-sm"><ExternalLink size={12}/> Abrir</button>
            </a>
          </div>
          <div className="card-body">
            <p className="dim" style={{fontSize:13}}>
              Visitantes acessando <code>https://{status.hostname}</code> veem uma página informativa indicando que aqui está hospedado um servidor Ookla.
            </p>
          </div>
        </div>
      )}

      {err && <div className="err-msg">{err}</div>}
      {out && <pre className="code" style={{maxHeight:240, overflow:'auto', fontSize:11.5}}>{out}</pre>}
    </div>
  );
}

function MinhaConexaoTab({ domain }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState('');
  const [err, setErr] = useState('');

  const load = () => api.get(`/mc/${domain.name}/status`).then(r => setStatus(r.data)).catch(()=>{});
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [domain.name]);

  const provision = async () => {
    if (!window.confirm(`Provisionar mc.${domain.name}? Cria DNS + emite cert SSL para a landing.`)) return;
    setBusy(true); setOut(''); setErr('');
    try {
      const r = await api.post(`/mc/${domain.name}/enable`, {});
      setOut(r.data.output || ''); load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro');
      setOut(e.response?.data?.output || e.message);
    } finally { setBusy(false); }
  };

  if (!status) return <div className="dim">Carregando status…</div>;

  const StepDot = ({ ok, n }) => (
    <div style={{
      width:28, height:28, borderRadius:'50%',
      background: ok ? 'var(--ok-dim)' : 'var(--bg-3)',
      color: ok ? 'var(--ok)' : 'var(--text-mute)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:'var(--font-mono)', fontWeight:700, fontSize:12,
      border: ok ? '1px solid rgba(63,185,80,0.3)' : '1px solid var(--border)',
      flexShrink:0,
    }}>{ok ? <CheckCircle2 size={16}/> : n}</div>
  );

  return (
    <div className="stack">
      <div>
        <h2 className="h2">Speedtest Server (Minha Conexão)</h2>
        <p className="dim" style={{fontSize:13, marginTop:4}}>
          Servidor oficial Minha Conexão em <code>mc.{domain.name}</code>.
          O daemon <code>mc-test</code> escuta TLS <code>9090</code> direto no host,
          com cert <code>*.mcservers.co</code> fornecido pela própria Minha Conexão.
        </p>
      </div>

      <div className="card">
        <div className="card-header">
          <Activity size={15} color="var(--accent)"/>
          <h3 className="h3">Progresso</h3>
          <span className={'badge ' + (status.daemon.port_9090 ? 'ok' : status.dns.ok ? 'warn' : 'muted')} style={{marginLeft:'auto'}}>
            {status.daemon.port_9090 ? 'Online' : status.dns.ok ? 'Em configuração' : 'Não provisionado'}
          </span>
        </div>
        <div className="card-body stack">
          {[
            { n:1, ok: status.dns.ok,         title:'DNS configurado',           detail: status.dns.ok ? `A: ${status.dns.a.join(', ')} · AAAA: ${status.dns.aaaa.join(', ') || '–'}` : `Criar mc.${domain.name}` },
            { n:2, ok: status.cert.present,   title:'Certificado SSL da landing', detail: status.cert.present ? `Let's Encrypt · válido até ${status.cert.expires}` : 'Sem cert ainda' },
            { n:3, ok: status.daemon.port_9090, title:'Daemon mc-test escutando 9090', detail: status.daemon.port_9090 ? `Service: ${status.daemon.service} · porta 9090 TLS respondendo` : 'Daemon parado ou não instalado' },
          ].map(s => (
            <div key={s.n} className="hrow" style={{gap:12, padding:'8px 0'}}>
              <StepDot ok={s.ok} n={s.n}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:500, fontSize:13.5}}>{s.title}</div>
                <div className="dim mono" style={{fontSize:11.5}}>{s.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(!status.dns.ok || !status.cert.present) ? (
        <div className="card">
          <div className="card-header">
            <Plus size={15} color="var(--accent)"/>
            <h3 className="h3">Provisionar mc.{domain.name}</h3>
          </div>
          <div className="card-body stack">
            <p className="dim" style={{fontSize:13, lineHeight:1.6}}>
              Cria o DNS A/AAAA e emite o certificado Let's Encrypt para a landing. O daemon mc-test
              precisa ser instalado separadamente no host (script oficial em <a href="https://www.minhaconexao.com.br/perguntas-frequentes/instalacao-servidor-testes" target="_blank" rel="noreferrer">minhaconexao.com.br</a>).
            </p>
            <div>
              <button className="btn btn-primary" onClick={provision} disabled={busy}>
                <Plus size={13}/> {busy ? 'Provisionando…' : 'Provisionar DNS + SSL'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <ExternalLink size={15} color="var(--accent)"/>
            <h3 className="h3">Cadastrar na Minha Conexão</h3>
            <a href={`https://${status.hostname}`} target="_blank" rel="noreferrer" className="card-actions">
              <button className="btn btn-secondary btn-sm"><ExternalLink size={12}/> Abrir landing</button>
            </a>
          </div>
          <div className="card-body stack">
            <p className="dim" style={{fontSize:13.5, lineHeight:1.6}}>
              Acesse <a href="https://www.minhaconexao.com.br/area-do-cliente" target="_blank" rel="noreferrer">minhaconexao.com.br/area-do-cliente</a> e cadastre seu
              servidor com os dados abaixo. Eles fazem a validação automática (porta 9090 deve estar acessível).
            </p>
            <div className="grid-2" style={{fontSize:13}}>
              {[
                ['Hostname',           status.hostname],
                ['IPv4 público',       status.ipv4],
                ['IPv6 público',       status.ipv6 || '—'],
                ['Porta',              '9090 (TLS)'],
                ['ASN',                'AS65000'],
                ['Organização',        'Hiperlink Telecom LTDA'],
              ].map(([k,v]) => (
                <div key={k} style={{padding:'10px 12px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8}}>
                  <div className="lbl">{k}</div>
                  <div className="mono" style={{fontSize:12.5, marginTop:4}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{padding:'12px 14px', background:'var(--info-dim)', border:'1px solid rgba(88,166,255,.25)', borderRadius:8, fontSize:13, color:'var(--text-dim)'}}>
              <strong style={{color:'var(--info)'}}>Validar:</strong> antes de cadastrar, teste a porta 9090 do seu lado.
              {' '}<code>curl https://{status.hostname}:9090/</code> deve retornar 200.
            </div>
          </div>
        </div>
      )}

      {err && <div className="err-msg">{err}</div>}
      {out && <pre className="code" style={{maxHeight:240, overflow:'auto', fontSize:11.5}}>{out}</pre>}
    </div>
  );
}

function Webmail({ domain, onChange }) {
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const enable = async () => {
    if (!window.confirm(`Habilitar webmail.${domain.name}? Será emitido certificado SSL via Let's Encrypt — o domínio precisa estar delegado e propagado.`)) return;
    setBusy(true); setOutput(''); setError('');
    try {
      const r = await api.post('/email/webmail/enable', { domain: domain.name });
      setOutput(r.data.output || ''); setError('');
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao habilitar webmail');
      setOutput(e.response?.data?.output || e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm(`Desabilitar webmail e remover o vhost de webmail.${domain.name}?`)) return;
    setBusy(true); setOutput(''); setError('');
    try {
      const r = await api.post('/email/webmail/disable', { domain: domain.name });
      setOutput(r.data.output || ''); setError('');
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.error || 'Erro');
      setOutput(e.response?.data?.output || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <h3>Webmail (Roundcube)</h3>
      <p style={{color:'#a1a1aa', fontSize:'.88rem'}}>
        Quando habilitado, fica em <code>https://webmail.{domain.name}</code> com certificado Let's Encrypt.
      </p>

      {domain.has_webmail ? (
        <>
          <span className="badge ok">Habilitado</span>
          <p style={{marginTop:'1rem', display:'flex', gap:'.5rem', alignItems:'center'}}>
            <a href={`https://webmail.${domain.name}`} target="_blank" rel="noreferrer">
              <button className="secondary"><ExternalLink size={12}/> Abrir webmail</button>
            </a>
            <button className="ghost" onClick={enable} disabled={busy}>Reemitir certificado</button>
            <button className="danger"  onClick={disable} disabled={busy}>Desabilitar</button>
          </p>
        </>
      ) : (
        <>
          <span className="badge warn">Desabilitado</span>
          <p style={{marginTop:'1rem'}}>
            <button onClick={enable} disabled={busy}>{busy ? 'Habilitando…' : 'Habilitar webmail'}</button>
            {' '}<button className="ghost" onClick={disable} disabled={busy} title="Limpa qualquer estado residual">Limpar estado</button>
          </p>
          <div style={{marginTop:'1rem', padding:'.8rem 1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'6px', fontSize:'.8rem', color:'#a1a1aa'}}>
            <strong style={{color:'#3b82f6'}}>Pré-requisitos:</strong>
            <ul style={{margin:'.3rem 0 0 1.2rem', padding:0}}>
              <li>Domínio <code>{domain.name}</code> precisa estar delegado aos seus NS</li>
              <li>DNS deve ter propagado (teste: <code>dig webmail.{domain.name} @1.1.1.1</code>)</li>
              <li>Porta 80 do IP {domain.ipv4} acessível pela internet (Let's Encrypt valida via HTTP)</li>
            </ul>
          </div>
        </>
      )}

      {error && (
        <div style={{marginTop:'1rem', padding:'.8rem 1rem', background:'rgba(239,68,68,.1)', border:'1px solid #ef4444', borderRadius:'6px'}}>
          <strong style={{color:'#ef4444'}}>✗ {error}</strong>
        </div>
      )}
      {output && (
        <>
          <h4 style={{marginTop:'1rem', marginBottom:'.4rem', color:'#a1a1aa', fontWeight:500, fontSize:'.85rem'}}>Saída do comando:</h4>
          <pre className="code" style={{maxHeight:'300px', overflow:'auto', fontSize:'.78rem'}}>{output}</pre>
        </>
      )}
    </>
  );
}

// ── NGINX PROXIES ───────────────────────────────────────────────
function Nginx({ domain }) {
  const [proxies, setProxies] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ subdomain: '', target_host: '', target_port: 80, target_protocol: 'http', ssl_enabled: false, websocket: false });
  const [msg, setMsg] = useState('');

  const load = () => api.get(`/nginx/${domain.name}/proxies`).then(r => setProxies(r.data));
  useEffect(() => { load(); }, [domain.name]);

  const add = async () => {
    setMsg('Salvando…');
    try {
      await api.post(`/nginx/${domain.name}/proxies`, f);
      setMsg('OK'); setShow(false);
      setF({ subdomain: '', target_host: '', target_port: 80, target_protocol: 'http', ssl_enabled: false, websocket: false });
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };
  const del = async (id) => {
    if (!window.confirm('Remover este proxy?')) return;
    await api.delete(`/nginx/proxies/${id}`); load();
  };

  return (
    <>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <h3 style={{margin:0}}>Proxies Reversos</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginTop:'.3rem'}}>Aponte subdomínios deste domínio para IPs/portas internos da rede.</p>
        </div>
        <button onClick={()=>setShow(true)}><Plus size={14}/> Adicionar Proxy</button>
      </div>

      <table style={{marginTop:'1rem'}}>
        <thead><tr><th>FQDN</th><th>Destino interno</th><th>Protocolo</th><th>SSL</th><th>WebSocket</th><th></th></tr></thead>
        <tbody>
          {proxies.map(p => (
            <tr key={p.id}>
              <td><strong>{p.subdomain ? `${p.subdomain}.${domain.name}` : domain.name}</strong></td>
              <td style={{fontFamily:'JetBrains Mono'}}>{p.target_protocol}://{p.target_host}:{p.target_port}</td>
              <td>{p.target_protocol}</td>
              <td>{p.ssl_enabled ? <span className="badge ok">on</span> : '–'}</td>
              <td>{p.websocket ? <span className="badge ok">on</span> : '–'}</td>
              <td><button className="danger" onClick={()=>del(p.id)}><Trash2 size={12}/></button></td>
            </tr>
          ))}
          {proxies.length === 0 && <tr><td colSpan={6} style={{color:'#a1a1aa'}}>Nenhum proxy.</td></tr>}
        </tbody>
      </table>

      {show && (
        <div className="modal-bg" onClick={()=>setShow(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h2>Novo Proxy Reverso</h2>
            <label>Subdomínio (vazio para domínio raiz)</label>
            <div style={{display:'flex', alignItems:'center', gap:'.5rem'}}>
              <input value={f.subdomain} onChange={e=>setF({...f, subdomain: e.target.value})} placeholder="app" style={{maxWidth:'200px'}} />
              <span style={{color:'#a1a1aa'}}>.{domain.name}</span>
            </div>
            <div className="row" style={{marginTop:'.6rem'}}>
              <div style={{flex:2}}>
                <label>Destino (IP interno ou hostname)</label>
                <input value={f.target_host} onChange={e=>setF({...f, target_host: e.target.value})} placeholder="192.168.1.10" />
              </div>
              <div style={{flex:1}}>
                <label>Porta</label>
                <input type="number" value={f.target_port} onChange={e=>setF({...f, target_port: +e.target.value})} />
              </div>
              <div style={{flex:1}}>
                <label>Protocolo</label>
                <select value={f.target_protocol} onChange={e=>setF({...f, target_protocol: e.target.value})}>
                  <option value="http">http</option><option value="https">https</option>
                </select>
              </div>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:'.5rem', marginTop:'.8rem'}}>
              <input type="checkbox" style={{width:'auto'}} checked={f.ssl_enabled} onChange={e=>setF({...f, ssl_enabled: e.target.checked})} />
              Emitir certificado SSL (Let's Encrypt) — exige DNS já propagado
            </label>
            <label style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
              <input type="checkbox" style={{width:'auto'}} checked={f.websocket} onChange={e=>setF({...f, websocket: e.target.checked})} />
              Habilitar WebSocket
            </label>
            {msg && <div style={{marginTop:'.6rem', color: msg.includes('Erro') ? '#ef4444':'#10b981'}}>{msg}</div>}
            <div className="modal-actions">
              <button className="secondary" onClick={()=>setShow(false)}>Cancelar</button>
              <button onClick={add} disabled={!f.target_host}>Criar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
